import { SuperAdminTransactions } from "./SuperAdminTransactions"
import { SuperAdminCompany, type CompanyCard } from "./SuperAdminCompany"
import { Avatar, date, Disclosure, Fields, FilterBar, linkRow, Paged, Pager, sentence, OpenCell, Panel as KitPanel, SearchField, StatusBadge, Table as KitTable } from "./adminUi"
import { formatAdminRoute, parseAdminRoute } from "src/utils/adminRoute"
import { askConfirm, askValue } from "./confirm"
import { EntityInput } from "./EntityInput"
import { SuperAdminFees } from "./SuperAdminFees"
import { T } from "../adminTheme"
import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import Input from "@ui/input/Input"
import Spacer from "@ui/spacer/Spacer"
import { Select } from "@ui/select/Select"
import { Typo } from "@ui/text/Typo"
import { Tile } from "@ui/tile/Tile"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import Config from "src/Config"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { DEFAULT_CURRENCY } from "src/constants"
import { parsePositiveAmountToMinorUnits } from "src/utils/amounts"
import { cardDisplayStatus } from "src/utils/cardStatus"
import { SuperAdminJobs } from "./SuperAdminJobs"
import { SuperAdminTurnover } from "./SuperAdminTurnover"
import { SuperAdminCrypto } from "./SuperAdminCrypto"
import { SuperAdminKyc } from "./SuperAdminKyc"
import { SuperAdminProviderBins } from "./SuperAdminProviderBins"
import { SuperAdminBalanceReconciliation } from "./SuperAdminBalanceReconciliation"
import { SuperAdminIssuerFunds } from "./SuperAdminIssuerFunds"
import { SuperAdminCardholders } from "./SuperAdminCardholders"
import { SuperAdminEvents } from "./SuperAdminEvents"
import { SuperAdminCardSpend } from "./SuperAdminCardSpend"
import { SuperAdminUserOverview } from "./SuperAdminUserOverview"
import { SuperAdminUserOverviewModal } from "./SuperAdminUserOverviewModal"
import { SuperAdminCardReconcile } from "./SuperAdminCardReconcile"
import { SuperAdminTransactionDetails } from "./SuperAdminTransactionDetails"
import {
	SuperAdminUserDirectory,
	type ISystemOverviewBalance,
	type ISystemUserDirectoryRow,
	type ISystemUserOverview
} from "./SuperAdminUserDirectory"
import {
	SUPER_ADMIN_SESSION_EXPIRED_EVENT,
	superAdminFetch as fetch
} from "src/services/superAdminFetch"
import {
	DEFAULT_COMPANY_DIRECTORY_FILTER,
	filterAndSortCompanies,
	type CompanyAccountTypeFilter,
	type CompanyDirectorySort,
	type CompanyRegistrationSourceFilter,
	type CompanyStatusFilter
} from "src/utils/companyDirectory"

interface IPartnerToken {
	kid: string
	secret_key: string
	partner_name: string
	status: "active" | "disabled" | string
	user_uid: string
	createdAt: string
	__actions?: null
}


interface ICompany {
	uid: string
	name: string
	number: string
	vat_number: string
	/** @deprecated Use balances; this scalar only represents the backend default issuer. */
	balance?: string
	balances?: ISystemIssuerBalance[]
	created_at: string
	status: "active" | "disabled" | string
	type?: "company" | "private"
	registration_source?: "backoffice" | "signup" | string
	referral_code?: string | null
	users_count?: number
	admins_count?: number
	buyers_count?: number
	teams_count?: number
	cards_count?: number
	active_cards_count?: number
	admins?: Array<{
		uid: string
		name: string
		email: string
		status: string
		company_status: string
		kyc_status: string
		last_login_at?: string | null
	}>
	issuers?: Array<{
		issuer: number
		issuer_label: string
		provider_code: string
		is_default: boolean
	}>
	deleted_at: string | null
	__actions?: null
}

interface IAdmin {
	uid: string
	company_uid: string
	company_status: "active" | "disabled"
	email: string
	name: string
	status: "active" | "disabled"
	created_at: string
	__actions?: null
}

type IUser = ISystemUserDirectoryRow

interface ICard {
	uid: string
	user_uid: string
	company_uid: string
	last4?: string
	mask?: string
	status: "active" | "disabled" | string
	issuer?: number
	provider_code?: string
	balance: string
	currency: string
	created_at: string
	blocked_at?: string | null
	/** True while cards/block runs; status stays active/frozen until the issuer confirms. */
	blocking?: boolean
	block_operation_uid?: string | null
	__actions?: null
}

interface ICommission {
	id: number | null
	company_uid: string
	provider_code: string
	type: string
	our_percent: number
	source?: "company" | "default"
	__actions?: null
}

interface IProviderMatrixRow {
	company_uid?: string
	company_name?: string
	provider_code?: string
	code?: string
	status?: string
	sort_order?: number
}

interface IProviderMatrixGroup extends IProviderMatrixRow {
	providers?: IProviderMatrixRow[]
}

const COMMISSION_PROVIDERS = ["cardspro", "wasabi", "wasabiprod"] as const
const COMMISSION_TYPES = [{ name: "Card top-up", value: "topup" }, { name: "Crypto top-up", value: "crypto_topup" }]
const providerName = (code: string) => PROVIDERS.find((item) => item.code === code)?.name ?? code

const PROVIDERS: ReadonlyArray<{
	code: string
	name: string
	description?: string
}> = [
	{ code: "cardspro", name: "Cardspro" },
	{ code: "wasabi", name: "Wasabi", description: "Sandbox Wasabi" },
	{
		code: "wasabiprod",
		name: "wasabiprod",
		description: "Production Wasabi"
	},
	{ code: "paytend", name: "Paytend" }
]

const COMPANY_PROVIDER_OPTIONS = [
	{ label: "All issuers", value: "all" },
	...PROVIDERS.map((provider) => ({ label: provider.name, value: provider.code }))
]

const getCompanyIssuerAccounts = (company?: Pick<ICompany, "balances"> | null) =>
	(company?.balances ?? []).filter(
		(account): account is ISystemIssuerBalance =>
			Number.isInteger(Number(account?.issuer)) &&
			Number(account.issuer) > 0 &&
			typeof account.currency === "string" &&
			Boolean(account.currency.trim())
	)

const getDefaultIssuerAccount = (company?: Pick<ICompany, "balances"> | null) => {
	const accounts = getCompanyIssuerAccounts(company)
	return accounts.find((account) => account.is_default) ?? accounts[0] ?? null
}

const getIssuerAccountLabel = (account: ISystemIssuerBalance) =>
	`${account.name || `Issuer ${account.issuer}`} · ${formatCurrencyWithNumberToSymbolWithNumber(
		Number(account.balance || 0),
		account.currency
	)}`

const getAdjustmentIssuerLabel = (account: ISystemIssuerBalance) =>
	account.balance === null
		? `${account.name || `Issuer ${account.issuer}`} · ${account.currency}`
		: getIssuerAccountLabel(account)

const getUserIssuerAccounts = (user: IUser, company?: ICompany) => {
	if (user.team_balances?.length) {
		return user.team_balances.map<ISystemIssuerBalance>((account) => ({
			issuer: account.issuer,
			currency: account.currency,
			balance: account.team_balance
		}))
	}
	return getCompanyIssuerAccounts(company).map((account) => ({ ...account, balance: null }))
}

const getCardIssuerAccounts = (card: ICard): ISystemIssuerBalance[] =>
	Number.isInteger(card.issuer)
		? [
				{
					issuer: Number(card.issuer),
					currency: card.currency || DEFAULT_CURRENCY,
					balance: Number(card.balance || 0)
				}
			]
		: []

type AdjustType = "card" | "company" | "buyer"
type AdjustDirection = "in" | "out"

interface ISystemIssuerBalance {
	issuer: number
	name?: string
	is_default?: boolean
	currency: string
	balance: number | string | null
}

const getOverviewIssuerAccounts = (accounts?: ISystemOverviewBalance[]) =>
	(accounts ?? [])
		.filter((account) => Number.isInteger(Number(account.issuer)) && Number(account.issuer) > 0 && Boolean(account.currency))
		.map<ISystemIssuerBalance>((account) => ({
			issuer: Number(account.issuer),
			name: account.issuer_label,
			is_default: account.is_default,
			currency: account.currency,
			balance: Number(account.amount)
		}))

interface IAdjustPopup {
	type: AdjustType
	direction: AdjustDirection
	uid: string
	name: string
	currentBalance: number | null
	currentCurrency: string
	issuer: number | null
	issuerOptions: ISystemIssuerBalance[]
	loadingBalance: boolean
}

type AdminTab =
	| "companies"
	| "create_company"
	| "create_private"
	| "create_admin"
	| "topup"
	| "company_admins"
	| "partner_tokens"
	| "create_partner_token"
	| "provider_balances"
	| "balances"
	| "users"
	| "user_overview"
	| "transactions"
	| "transaction_details"
	| "cards"
	| "cardholders"
	| "events"
	| "commissions"
	| "fees"
	| "providers"
	| "kyc"
	| "crypto"
	| "jobs"
	| "turnover"
	| "company"

const SA = {
	canvas: T.canvas,
	surface: T.surface,
	surfaceSoft: T.surface2,
	text: T.text,
	muted: T.muted,
	border: T.border,
	accent: T.primary,
	accentDark: T.accentText,
	accentSoft: T.accentSoft,
	warning: T.warn,
	danger: T.danger,
	shadow: T.shadowSm
} as const

const SA_COMPACT = "@media (max-width: 1040px)"
const SA_MOBILE = "@media (max-width: 720px)"
const SA_PANEL_SX = { backgroundColor: SA.surface, border: `1px solid ${SA.border}`, borderRadius: "14px", boxShadow: SA.shadow }
const MINOR_UNIT_FACTOR = 100
const PERCENT_DECIMAL_PLACES = 2

const COMPANY_ACCOUNT_TYPE_OPTIONS = [
	{ label: "All account types", value: "all" },
	{ label: "Companies", value: "company" },
	{ label: "Private accounts", value: "private" }
] as const

const COMPANY_STATUS_OPTIONS = [
	{ label: "All statuses", value: "all" },
	{ label: "Active", value: "active" },
	{ label: "Disabled", value: "disabled" },
	{ label: "Deleted", value: "deleted" }
] as const

const COMPANY_REGISTRATION_SOURCE_OPTIONS = [
	{ label: "All sources", value: "all" },
	{ label: "Backoffice", value: "backoffice" },
	{ label: "Signup", value: "signup" }
] as const

const COMPANY_SORT_OPTIONS = [
	{ label: "Newest first", value: "created_desc" },
	{ label: "Oldest first", value: "created_asc" },
	{ label: "Name A–Z", value: "name_asc" },
	{ label: "Name Z–A", value: "name_desc" }
] as const

const ADMIN_TAB_META: Record<AdminTab, { eyebrow: string; title: string; description: string }> = {
	companies: { eyebrow: "Find a client", title: "Clients", description: "Search by email, name or UID, then click a row to see everything about the client." },
	create_company: { eyebrow: "Companies", title: "Create company", description: "Add a company account." },
	create_private: { eyebrow: "Companies", title: "Create private account", description: "Add a private account and its first admin." },
	create_admin: { eyebrow: "Access", title: "Create company admin", description: "Add an admin to a company." },
	topup: { eyebrow: "Money", title: "Fund balance", description: "Top up a client balance at one issuer. Enter the amount in dollars; it is sent in cents." },
	company_admins: { eyebrow: "Settings", title: "Company admins", description: "View and manage company admins." },
	partner_tokens: { eyebrow: "Settings", title: "Partner API tokens", description: "View and manage partner API tokens." },
	create_partner_token: { eyebrow: "Access", title: "Create partner token", description: "Create an API token for a partner." },
	provider_balances: { eyebrow: "Money", title: "Issuer balances", description: "Our real account at each issuer next to what companies, buyers and cards hold on it." },
	balances: { eyebrow: "Money", title: "Balance check", description: "Compare saved balances with transaction balances." },
	users: { eyebrow: "Find a client", title: "Users", description: "Find users and check their status." },
	user_overview: { eyebrow: "Investigate", title: "Find user by UID", description: "Inspect access, issuer balances, card holders, KYC and blocking flow in one place." },
	transactions: { eyebrow: "Money", title: "Transactions", description: "Inspect movements by company, owner, card and issuer." },
	transaction_details: { eyebrow: "Investigate", title: "Find transaction by ID", description: "Inspect TRX, issuer archive, live response and saved webhook by operation ID." },
	cards: { eyebrow: "Find a client", title: "Cards", description: "View card owners, status and balance." },
	cardholders: { eyebrow: "Investigate", title: "Card holders", description: "Inspect, copy and move issuer identities between users." },
	events: { eyebrow: "Investigate", title: "Event log", description: "Review critical failures and important system actions." },
	commissions: { eyebrow: "Money", title: "Our rates (old)", description: "Legacy markup per company. The new tariff is in Fees." },
	fees: { eyebrow: "Money", title: "Fees", description: "The general tariff: what every client pays by default, by company type and by issuer." },
	providers: { eyebrow: "Settings", title: "Issuer access", description: "Choose which issuers each client can use and in what order." },
	kyc: { eyebrow: "Investigate", title: "KYC", description: "Check identity verification and fix issues." },
	company: { eyebrow: "Find a client", title: "Client", description: "Everything about one client: users, KYC, cards, crypto, transactions, events and our rates." },
	turnover: { eyebrow: "Money", title: "Client turnover", description: "Deposits, card spend and issuer fees by period, type and company." },
	jobs: { eyebrow: "Settings", title: "Background jobs", description: "View background tasks running on this instance." },
	crypto: { eyebrow: "Money", title: "Crypto deposits", description: "View crypto addresses, deposits and transfers." }
}

/** Grouped by what support is trying to do, not by backend table. */
const ADMIN_NAV: Array<{ label: string; items: Array<{ tab: AdminTab; label: string; icon: AdminIconName }> }> = [
	{ label: "Find a client", items: [
		{ tab: "companies", label: "Clients", icon: "companies" },
		{ tab: "users", label: "Users", icon: "user" },
		{ tab: "cards", label: "Cards", icon: "card" }
	] },
	{ label: "Money", items: [
		{ tab: "topup", label: "Fund balance", icon: "plus" },
		{ tab: "transactions", label: "Transactions", icon: "receipt" },
		{ tab: "turnover", label: "Client turnover", icon: "chart" },
		{ tab: "crypto", label: "Crypto deposits", icon: "crypto" },
		{ tab: "fees", label: "Fees", icon: "percent" },
		{ tab: "commissions", label: "Our rates (old)", icon: "chart" },
		{ tab: "provider_balances", label: "Issuer balances", icon: "wallet" },
		{ tab: "balances", label: "Balance check", icon: "scale" }
	] },
	{ label: "Investigate", items: [
		{ tab: "kyc", label: "KYC", icon: "shield" },
		{ tab: "cardholders", label: "Card holders", icon: "idcard" },
		{ tab: "events", label: "Event log", icon: "pulse" },
		{ tab: "transaction_details", label: "Find transaction by ID", icon: "search" },
		{ tab: "user_overview", label: "Find user by UID", icon: "search" }
	] },
	{ label: "Settings", items: [
		{ tab: "company_admins", label: "Company admins", icon: "team" },
		{ tab: "providers", label: "Issuer access", icon: "provider" },
		{ tab: "partner_tokens", label: "Partner API tokens", icon: "key" },
		{ tab: "jobs", label: "Background jobs", icon: "cpu" }
	] }
]

export const SuperAdminPage = () => {
	const [token, setToken] = useState<string | null>(localStorage.getItem("sa_token"))
	const [loading, setLoading] = useState(false)
	const [loginStep, setLoginStep] = useState<"credentials" | "otp">("credentials")
	const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null)
	const [initialRoute] = useState(() => parseAdminRoute(window.location.hash, (tab) => tab in ADMIN_TAB_META))
	const [activeTab, setActiveTab] = useState<AdminTab>(initialRoute.tab as AdminTab)
	const [companyRoute, setCompanyRoute] = useState({ uid: initialRoute.company ?? "", section: initialRoute.section, card: initialRoute.card })
	const [companies, setCompanies] = useState<ICompany[]>([])
	const [companyDirectoryFilter, setCompanyDirectoryFilter] = useState(() => ({
		...DEFAULT_COMPANY_DIRECTORY_FILTER
	}))
	const [admins, setAdmins] = useState<IAdmin[]>([])
	const [partnerTokens, setPartnerTokens] = useState<IPartnerToken[]>([])
	const [selectedCompanyForAdmins, setSelectedCompanyForAdmins] =
		useState<ICompany | null>(null)

	const [users, setUsers] = useState<IUser[]>([])
	const [usersCompanyFilter, setUsersCompanyFilter] = useState<string>("")
	const [overviewModalUid, setOverviewModalUid] = useState<string | null>(initialRoute.user ?? null)
	const [eventsInitialFilters, setEventsInitialFilters] = useState<{ company_uid?: string; user?: string }>({})

	const [cards, setCards] = useState<ICard[]>([])
	const [cardsCompanyFilter, setCardsCompanyFilter] = useState<string>("")
	const [cardsUserFilter, setCardsUserFilter] = useState<string>("")
	const [cardsUidQuery, setCardsUidQuery] = useState("")
	const [cardsStatusFilter, setCardsStatusFilter] = useState("")
	const [cardsTotal, setCardsTotal] = useState(0)
	const [cardsOffset, setCardsOffset] = useState(0)
	const cardsRequestId = useRef(0)
	// Read through a ref so every fetchCards caller keeps the chosen order without a new argument.
	const cardsSortRef = useRef<"" | "balance">("")
	const [cardsSort, setCardsSort] = useState<"" | "balance">("")
	const [spendCard, setSpendCard] = useState<ICard | null>(null)
	const [reconcileCard, setReconcileCard] = useState<ICard | null>(null)

	const [commissions, setCommissions] = useState<ICommission[]>([])
	const [commissionsCompany, setCommissionsCompany] = useState<ICompany | null>(null)
	const [commissionForm, setCommissionForm] = useState({
		provider_code: "cardspro",
		type: "topup",
		our_percent: ""
	})
	const [commissionLoading, setCommissionLoading] = useState(false)

	const [providerMatrix, setProviderMatrix] = useState<IProviderMatrixRow[]>([])
	const [providersCompany, setProvidersCompany] = useState<ICompany | null>(null)
	const [providersLoading, setProvidersLoading] = useState(false)
	const [providerSortOrders, setProviderSortOrders] = useState<Record<string, string>>({})

	const [adjustPopup, setAdjustPopup] = useState<IAdjustPopup | null>(null)
	const [adjustForm, setAdjustForm] = useState({
		amount: "",
		currency: "USD"
	})
	const [adjustLoading, setAdjustLoading] = useState(false)

	const [createPartnerTokenForm, setCreatePartnerTokenForm] = useState({
		partner_name: "",
		user_uid: "",
		expires_at: ""
	})

	const [loginForm, setLoginForm] = useState({
		email: "",
		password: ""
	})
	const [otpForm, setOtpForm] = useState({
		email: "",
		code: ""
	})

	const [topupForm, setTopupForm] = useState({
		companyUid: "",
		issuer: null as number | null,
		amount: "",
		currency: "USD"
	})

	const [createCompanyForm, setCreateCompanyForm] = useState({
		name: "",
		number: "",
		vat_number: "",
		type: "company" as "company" | "private"
	})

	const [createPrivateForm, setCreatePrivateForm] = useState({
		name: "",
		number: "",
		vat_number: "",
		admin_name: "",
		admin_email: "",
		admin_password: ""
	})

	const [createAdminForm, setCreateAdminForm] = useState({
		company_uid: "",
		name: "",
		email: "",
		password: ""
	})

	useEffect(() => {
		const handleExpiredSession = () => {
			localStorage.removeItem("sa_token")
			setToken(null)
			setLoading(false)
			setLoginStep("credentials")
			setOtpForm({ email: "", code: "" })
			setOtpExpiresAt(null)
		}

		window.addEventListener(SUPER_ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession)
		return () =>
			window.removeEventListener(SUPER_ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession)
	}, [])

	const updateForm =
		<T extends object>(setter: React.Dispatch<React.SetStateAction<T>>) =>
		<K extends keyof T>(key: K) =>
		(value: T[K]) => {
			setter((prev) => ({ ...prev, [key]: value }))
		}

	const completeLogin = (nextToken: string) => {
		setToken(nextToken)
		localStorage.setItem("sa_token", nextToken)
		setLoginStep("credentials")
		setOtpForm({ email: "", code: "" })
		setOtpExpiresAt(null)
	}

	const fetchCompanies = async () => {
		if (!token) return
		try {
			const loadPage = async (offset: number) => {
				const res = await fetch(Config.scheme.URL_API + "system/company/list", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify({ limit: 1000, offset, with_meta: true })
				})
				const payload = await res.json()
				if (Array.isArray(payload.data)) return { rows: payload.data as ICompany[], total: payload.data.length }
				return {
					rows: Array.isArray(payload.data?.rows) ? payload.data.rows as ICompany[] : [],
					total: Number(payload.data?.total ?? 0)
				}
			}
			const first = await loadPage(0)
			let rows = first.rows
			while (rows.length < first.total) {
				const next = await loadPage(rows.length)
				if (!next.rows.length) break
				rows = rows.concat(next.rows)
			}
			if (rows.length || first.total === 0) {
				const nextCompanies = rows.map((company) => ({
					...company,
					// The scalar company balance is not a money source. Only issuer accounts,
					// when returned by an older compatible backend, may drive money actions.
					balance: company.balances?.length ? String(getDefaultIssuerAccount(company)?.balance ?? 0) : undefined,
					__actions: null
				}))
				setCompanies(nextCompanies)
			}
		} catch (e) {
			console.error(e)
		}
	}

	const fetchPartnerTokens = async () => {
		if (!token) return
		try {
			const res = await fetch(Config.scheme.URL_API + "system/partner/token/list", {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				}
			})
			const data = await res.json()
			if (data.data) {
				data.data.forEach((t: IPartnerToken) => {
					t.__actions = null
				})
				setPartnerTokens(data.data)
			}
		} catch (e) {
			console.error(e)
		}
	}

	useEffect(() => {
		if (token) {
			fetchCompanies()
			fetchPartnerTokens()
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token])

	const handleLogin = async () => {
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/login", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(loginForm)
			})
			const data = await res.json()
			if (data.data?.otp_sent) {
				setLoginStep("otp")
				setOtpForm({
					email: data.data.email || loginForm.email,
					code: ""
				})
				setOtpExpiresAt(data.data.expires_at || null)
				toast.success("Confirmation code sent to email")
			} else if (data.data?.token) {
				completeLogin(data.data.token)
				toast.success("Login successful")
			} else {
				console.error(data)
				toast.error("Login failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Login error")
		} finally {
			setLoading(false)
		}
	}

	const handleVerifyLogin = async () => {
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/login/verify", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(otpForm)
			})
			const data = await res.json()
			if (data.data?.token) {
				completeLogin(data.data.token)
				toast.success("Login successful")
			} else {
				console.error(data)
				toast.error("Verification failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Verification error")
		} finally {
			setLoading(false)
		}
	}

	const handleTopup = async () => {
		if (!token) return
		const company = companies.find((item) => item.uid === topupForm.companyUid)
		const issuerAccount = getCompanyIssuerAccounts(company).find(
			(account) => account.issuer === topupForm.issuer
		)
		if (!company || !issuerAccount) {
			toast.warning("Select a company issuer account")
			return
		}
		const parsedAmount = parsePositiveAmountToMinorUnits(topupForm.amount)
		if (typeof parsedAmount.amount !== "number") {
			toast.warning(parsedAmount.error || "Enter a valid amount")
			return
		}
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/company/topup", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({
					company_uid: topupForm.companyUid,
					issuer: issuerAccount.issuer,
					amount: parsedAmount.amount,
					currency: issuerAccount.currency
				})
			})
			const data = await res.json()
			if (data.data) {
				toast.success("Topup successful")
				fetchCompanies()
				setTopupForm({ companyUid: "", issuer: null, amount: "", currency: "USD" })
			} else {
				console.error(data)
				toast.error("Topup failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Topup error")
		} finally {
			setLoading(false)
		}
	}

	const handleCreateCompany = async () => {
		if (!token) return
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/company/create", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(createCompanyForm)
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Company ${data.data.name} created!`)
				fetchCompanies()
				setCreateCompanyForm({ name: "", number: "", vat_number: "", type: "company" })
				setActiveTab("companies")
			} else {
				console.error(data)
				toast.error("Create company failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Create company error")
		} finally {
			setLoading(false)
		}
	}

	const handleCreatePrivate = async () => {
		if (!token) return
		if (!createPrivateForm.name.trim()) return toast.warning("Company name is required")
		if (!createPrivateForm.admin_email.trim()) return toast.warning("Admin email is required")
		if (!createPrivateForm.admin_password) return toast.warning("Admin password is required")
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/company/create-private", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(createPrivateForm)
			})
			const data = await res.json()
			if (data.data?.company) {
				toast.success(
					`Private company ${data.data.company.name} created with admin ${data.data.admin?.email ?? ""}`
				)
				fetchCompanies()
				setCreatePrivateForm({
					name: "",
					number: "",
					vat_number: "",
					admin_name: "",
					admin_email: "",
					admin_password: ""
				})
				setActiveTab("companies")
			} else {
				console.error(data)
				toast.error(
					"Create private company failed: " + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error("Create private company error")
		} finally {
			setLoading(false)
		}
	}

	const handleCompanyStateChange = async (uid: string, action: "enable" | "disable") => {
		if (!token) return
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + `system/company/${action}`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ uid, currency: "USD" })
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Company ${action}d successfully`)
				fetchCompanies()
			} else {
				toast.error(
					`Failed to ${action} company: ` + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error(`Error: ${e}`)
		} finally {
			setLoading(false)
		}
	}

	const handleCreatePartnerToken = async () => {
		if (!token) return
		setLoading(true)
		try {
			// Ensure expires_at is ISO formatted for dates
			const payload: { partner_name: string; user_uid: string; expires_at?: string } = { ...createPartnerTokenForm }
			if (payload.expires_at) {
				const d = new Date(payload.expires_at)
				if (!isNaN(d.getTime())) {
					payload.expires_at = d.toISOString()
				}
			} else {
				delete payload.expires_at
			}

			const res = await fetch(Config.scheme.URL_API + "system/partner/token/create", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(payload)
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Partner token created!`)
				fetchPartnerTokens()
				setCreatePartnerTokenForm({ partner_name: "", user_uid: "", expires_at: "" })
				setActiveTab("partner_tokens")
			} else {
				console.error(data)
				toast.error(
					"Create partner token failed: " + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error("Create partner token error")
		} finally {
			setLoading(false)
		}
	}

	const handlePartnerTokenAction = async (
		kid: string,
		action: "enable" | "disable" | "delete"
	) => {
		if (!token) return
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + `system/partner/token/${action}`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ kid })
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Partner token ${action}d successfully`)
				fetchPartnerTokens()
			} else {
				toast.error(
					`Failed to ${action} partner token: ` + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error(`Error: ${e}`)
		} finally {
			setLoading(false)
		}
	}

	const fetchAdmins = async (companyUid: string) => {
		if (!token) return
		setLoading(true)
		try {
			const url = new URL(Config.scheme.URL_API + "system/admin/list")
			url.searchParams.append("company_uid", companyUid)

			const res = await fetch(url.toString(), {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				}
			})
			const data = await res.json()
			if (data.data) {
				data.data.forEach((a: IAdmin) => {
					a.__actions = null
				})
				setAdmins(data.data)
			} else {
				// Handle array or object response depending on API
				setAdmins([])
			}
		} catch (e) {
			console.error(e)
			setAdmins([])
		} finally {
			setLoading(false)
		}
	}

	const handleAdminStateChange = async (
		userUid: string,
		companyUid: string,
		action: "enable" | "disable"
	) => {
		if (!token) return
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + `system/admin/${action}`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ user_uid: userUid, company_uid: companyUid })
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Admin ${action}d successfully`)
				fetchAdmins(companyUid)
			} else {
				toast.error(
					`Failed to ${action} admin: ` + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error(`Error: ${e}`)
		} finally {
			setLoading(false)
		}
	}

	const handleChangeAdminPassword = async (
		userUid: string,
		companyUid: string,
		newPass: string
	) => {
		if (!token) return
		if (!newPass) return toast.warning("Password cannot be empty")
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/admin/change-password", {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({
					user_uid: userUid,
					company_uid: companyUid,
					new_password: newPass
				})
			})
			const data = await res.json()
			if (data.data) {
				toast.success("Password changed successfully")
				// setChangePasswordForm({ user_uid: "", new_password: "" });
			} else {
				toast.error(
					"Failed to change password: " + (data.error?.message || "Unknown error")
				)
			}
		} catch (e) {
			console.error(e)
			toast.error(`Error: ${e}`)
		} finally {
			setLoading(false)
		}
	}

	const handleCreateAdmin = async () => {
		if (!token) return
		setLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/admin/create", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(createAdminForm)
			})
			const data = await res.json()
			if (data.data) {
				toast.success(`Admin ${data.data.name} created!`)
				const createdCompanyUid = createAdminForm.company_uid
				setCreateAdminForm({ company_uid: "", name: "", email: "", password: "" })

				if (
					selectedCompanyForAdmins &&
					selectedCompanyForAdmins.uid === createdCompanyUid
				) {
					setActiveTab("company_admins")
					fetchAdmins(createdCompanyUid)
				} else {
					setActiveTab("companies")
				}
			} else {
				console.error(data)
				toast.error("Create admin failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Create admin error")
		} finally {
			setLoading(false)
		}
	}

	const fetchUsers = async (companyUid?: string) => {
		if (!token) return
		setLoading(true)
		try {
			const loadPage = async (offset: number) => {
				const res = await fetch(Config.scheme.URL_API + "system/user/list", {
					method: "POST",
					headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
					body: JSON.stringify({ ...(companyUid ? { company_uid: companyUid } : {}), limit: 1000, offset, with_meta: true })
				})
				const payload = await res.json()
				if (Array.isArray(payload.data)) return { rows: payload.data as IUser[], total: payload.data.length }
				return {
					rows: Array.isArray(payload.data?.rows) ? payload.data.rows as IUser[] : [],
					total: Number(payload.data?.total ?? 0)
				}
			}
			const first = await loadPage(0)
			let rows = first.rows
			while (rows.length < first.total) {
				const next = await loadPage(rows.length)
				if (!next.rows.length) break
				rows = rows.concat(next.rows)
			}
			setUsers(rows.map((user) => ({ ...user, __actions: null })))
		} catch (e) {
			console.error(e)
			setUsers([])
		} finally {
			setLoading(false)
		}
	}

	const fetchUserOverview = async (userUid: string): Promise<ISystemUserOverview> => {
		if (!token) throw new Error("System session is unavailable")
		const response = await fetch(Config.scheme.URL_API + "system/user/overview", {
			method: "POST",
			headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			body: JSON.stringify({ user_uid: userUid, refresh: false, with_transactions: false, transactions_limit: 50 })
		})
		const payload = await response.json()
		if (!payload.data) throw new Error(payload.error?.message || "Financial accounts could not be loaded")
		return payload.data as ISystemUserOverview
	}

	const fetchCompanyFinancialAccounts = async (company: ICompany) => {
		const cached = getCompanyIssuerAccounts(company)
		if (cached.length) return cached
		const adminUid = company.admins?.[0]?.uid
		if (!adminUid) throw new Error("The company has no administrator whose financial overview can be loaded")
		const overview = await fetchUserOverview(adminUid)
		const accounts = getOverviewIssuerAccounts(overview.balances?.company)
		if (!accounts.length) throw new Error("The company overview contains no issuer wallets")
		setCompanies((current) => current.map((item) => item.uid === company.uid ? { ...item, balances: accounts } : item))
		return accounts
	}

	const fetchCards = async (companyUid?: string, userUid?: string, search = cardsUidQuery, status = cardsStatusFilter, offset = 0) => {
		if (!token) return
		const requestId = ++cardsRequestId.current
		setLoading(true)
		try {
			const response = await systemPost<{ rows: ICard[]; total: number; offset: number }>(token, "system/cards/list", {
				company_uid: companyUid || "", user_uid: userUid || "", search: search.trim(), status,
				with_meta: true, limit: 50, offset,
				...(cardsSortRef.current === "balance" ? { sort: "balance", order: "desc" } : {})
			})
			if (requestId !== cardsRequestId.current) return
			if (response.error) throw new Error(systemErrorMessage(response.error))
			const result = response.data
			if (result) {
				(result.rows ?? []).forEach((c) => {
					c.__actions = null
				})
				setCards(result.rows ?? [])
				setCardsTotal(result.total ?? 0)
				setCardsOffset(result.offset ?? offset)
			} else {
				setCards([])
				setCardsTotal(0)
			}
		} catch (e) {
			if (requestId !== cardsRequestId.current) return
			console.error(e)
			setCards([])
			toast.error(e instanceof Error ? e.message : "Cards request failed")
		} finally {
			if (requestId === cardsRequestId.current) setLoading(false)
		}
	}

	useEffect(() => {
		if (activeTab !== "cards") return
		const timeout = window.setTimeout(() => {
			void fetchCards(cardsCompanyFilter || undefined, cardsUserFilter || undefined, cardsUidQuery, cardsStatusFilter)
		}, 300)
		return () => window.clearTimeout(timeout)
	}, [cardsUidQuery, activeTab, token]) // eslint-disable-line react-hooks/exhaustive-deps
	// Load on screen open too, so links and Back land on a filled list.
	useEffect(() => {
		if (activeTab === "cards" && token) void fetchUsers(cardsCompanyFilter || undefined)
	}, [activeTab, token]) // eslint-disable-line react-hooks/exhaustive-deps

	const fetchCommissions = async (companyUid: string) => {
		if (!token) return
		setCommissionLoading(true)
		try {
			const url = new URL(Config.scheme.URL_API + "system/commissions/effective")
			url.searchParams.set("company_uid", companyUid)
			const res = await fetch(url.toString(), {
				method: "GET",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
			})
			const data = await res.json()
			if (data.data) {
				data.data.forEach((c: ICommission) => {
					c.__actions = null
				})
				setCommissions(data.data)
			} else {
				setCommissions([])
			}
		} catch (e) {
			console.error(e)
			setCommissions([])
		} finally {
			setCommissionLoading(false)
		}
	}

	const handleSetCommission = async () => {
		if (!token || !commissionsCompany) return
		if (!commissionForm.our_percent) return toast.warning("Please enter our percent")
		setCommissionLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/commissions/set", {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({
					company_uid: commissionsCompany.uid,
					provider_code: commissionForm.provider_code,
					type: commissionForm.type,
					our_percent: Math.round(Number(commissionForm.our_percent) * MINOR_UNIT_FACTOR)
				})
			})
			const data = await res.json()
			if (data.data) {
				toast.success("Commission saved")
				setCommissionForm({ provider_code: "cardspro", type: "topup", our_percent: "" })
				fetchCommissions(commissionsCompany.uid)
			} else {
				toast.error("Failed to save: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Error saving commission")
		} finally {
			setCommissionLoading(false)
		}
	}

	const handleDeleteCommission = async (id: number | null) => {
		if (!token || !commissionsCompany || id === null) return
		setCommissionLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/commissions/delete", {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ company_uid: commissionsCompany.uid, id })
			})
			const data = await res.json()
			if (data.data) {
				toast.success("Commission deleted")
				fetchCommissions(commissionsCompany.uid)
			} else {
				toast.error("Failed to delete: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Error deleting commission")
		} finally {
			setCommissionLoading(false)
		}
	}

	const fetchProviderMatrix = async (companyUid: string) => {
		if (!token) return
		setProvidersLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/companies/providers/list", {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ company_uid: companyUid })
			})
			const data = await res.json()
			if (data.data) {
				// Be defensive about the response shape. Accept either a flat list of
				// { company_uid, provider_code, status } rows, OR per-company grouped
				// objects like { company_uid, providers: [{ provider_code, status }] }.
				const raw = (Array.isArray(data.data) ? data.data : []) as IProviderMatrixGroup[]
				const flat: IProviderMatrixRow[] = []
				for (const item of raw) {
					if (item && Array.isArray(item.providers)) {
						for (const p of item.providers) {
							flat.push({
								company_uid: item.company_uid,
								company_name: item.company_name,
								provider_code: p.provider_code ?? p.code,
								status: p.status,
								sort_order: p.sort_order
							})
						}
					} else {
						flat.push(item)
					}
				}
				setProviderMatrix(flat)
				setProviderSortOrders(
					Object.fromEntries(
						flat
							.filter((row) => row.provider_code)
							.map((row, index) => [
								row.provider_code as string,
								String(row.sort_order ?? index)
							])
					)
				)
			} else {
				setProviderMatrix([])
				setProviderSortOrders({})
			}
		} catch (e) {
			console.error(e)
			setProviderMatrix([])
		} finally {
			setProvidersLoading(false)
		}
	}

	const handleProviderToggle = async (
		companyUid: string,
		providerCode: string,
		enabled: boolean
	) => {
		if (!token) return
		const existing = providerMatrix.find(
			(row) =>
				(row.provider_code === providerCode || row.code === providerCode) &&
				(!row.company_uid || row.company_uid === companyUid)
		)
		const activeOrders = providerMatrix
			.filter((row) => row.status === "active" && Number.isInteger(Number(row.sort_order)))
			.map((row) => Number(row.sort_order))
		const nextAvailableOrder = activeOrders.length ? Math.max(...activeOrders) + 1 : 0
		const sortOrder = Number(
			providerSortOrders[providerCode] ?? existing?.sort_order ?? nextAvailableOrder
		)
		if (!Number.isInteger(sortOrder) || sortOrder < 0) {
			toast.warning("Sort order must be a non-negative integer")
			return
		}
		setProvidersLoading(true)
		try {
			const res = await fetch(Config.scheme.URL_API + "system/companies/providers/set", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify({
						company_uid: companyUid,
						provider_code: providerCode,
						status: enabled ? "active" : "disabled",
						sort_order: sortOrder
					})
				})
			const data = await res.json()
			if (data.data) {
				toast.success(
					`Provider ${providerCode} ${enabled ? "enabled" : "disabled"}`
				)
				fetchProviderMatrix(companyUid)
			} else {
				toast.error("Failed to update provider: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Error updating provider")
		} finally {
			setProvidersLoading(false)
		}
	}

	const openAdjustPopup = (
		type: AdjustType,
		direction: AdjustDirection,
		uid: string,
		name: string,
		currentBalance: number | null = null,
		currentCurrency = "USD",
		issuerOptions: ISystemIssuerBalance[] = [],
		issuer: number | null = null
	) => {
		const selectedAccount =
			issuerOptions.find((account) => account.issuer === issuer) ?? issuerOptions[0] ?? null
		setAdjustPopup({
			type,
			direction,
			uid,
			name,
			currentBalance: selectedAccount ? Number(selectedAccount.balance || 0) : currentBalance,
			currentCurrency: selectedAccount?.currency ?? currentCurrency,
			issuer: selectedAccount?.issuer ?? issuer,
			issuerOptions,
			loadingBalance: false
		})
		setAdjustForm({ amount: "", currency: selectedAccount?.currency ?? currentCurrency })
	}

	const openBuyerAdjustPopup = async (direction: AdjustDirection, user: IUser) => {
		try {
			const company = companies.find((item) => item.uid === user.company_uid)
			let options = getUserIssuerAccounts(user, company)
			if (!user.team_balances?.length) {
				const overview = await fetchUserOverview(user.uid)
				options = getOverviewIssuerAccounts(overview.balances?.buyer)
			}
			if (!options.length) throw new Error("This user has no buyer issuer wallets")
			const defaultIssuer = options.find((account) => account.is_default)?.issuer ?? getDefaultIssuerAccount(company)?.issuer
			const selected = options.find((account) => account.issuer === defaultIssuer) ?? options[0]
			openAdjustPopup(
				"buyer",
				direction,
				user.uid,
				user.name,
				selected?.balance === null || selected?.balance === undefined ? null : Number(selected.balance),
				selected?.currency ?? DEFAULT_CURRENCY,
				options,
				selected?.issuer ?? null
			)
		} catch (cause) {
			toast.error(cause instanceof Error ? cause.message : "Buyer wallets could not be loaded")
		}
	}

	const openCompanyAdjustPopup = async (direction: AdjustDirection, company: ICompany) => {
		try {
			const options = await fetchCompanyFinancialAccounts(company)
			const selected = options.find((account) => account.is_default) ?? options[0]
			openAdjustPopup("company", direction, company.uid, company.name, selected.balance === null ? null : Number(selected.balance), selected.currency, options, selected.issuer)
		} catch (cause) {
			toast.error(cause instanceof Error ? cause.message : "Company wallets could not be loaded")
		}
	}

	const openCompanyTopup = async (company: ICompany) => {
		try {
			const options = await fetchCompanyFinancialAccounts(company)
			const selected = options.find((account) => account.is_default) ?? options[0]
			setTopupForm((previous) => ({ ...previous, companyUid: company.uid, issuer: selected.issuer, currency: selected.currency }))
			setActiveTab("topup")
		} catch (cause) {
			toast.error(cause instanceof Error ? cause.message : "Company wallets could not be loaded")
		}
	}

	const openCardAdjustPopup = (direction: AdjustDirection, card: ICard) => {
		const options = getCardIssuerAccounts(card)
		const selected = options[0]
		openAdjustPopup(
			"card",
			direction,
			card.uid,
			card.mask || `•••• ${card.last4 ?? card.uid}`,
			Number(card.balance),
			card.currency || DEFAULT_CURRENCY,
			options,
			selected?.issuer ?? null
		)
	}

	const handleAdjustment = async () => {
		if (!adjustPopup || !token) return
		if (!Number.isInteger(adjustPopup.issuer)) {
			toast.warning("Select an issuer account")
			return
		}
		const parsedAmount = parsePositiveAmountToMinorUnits(adjustForm.amount)
		if (typeof parsedAmount.amount !== "number")
			return toast.warning(parsedAmount.error || "Enter a valid amount.")
		setAdjustLoading(true)
		try {
			const res = await fetch(
				Config.scheme.URL_API +
					`system/${adjustPopup.type}/balance/adjust/${adjustPopup.direction}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`
					},
					body: JSON.stringify({
						uid: adjustPopup.uid,
						issuer: adjustPopup.issuer,
						amount: parsedAmount.amount,
						currency: adjustPopup.currentCurrency || "USD"
					})
				}
			)
			const data = await res.json()
			if (data.data) {
				toast.success("Balance adjusted successfully")
				setAdjustPopup(null)
				fetchCompanies()
			} else {
				toast.error("Adjustment failed: " + (data.error?.message || "Unknown error"))
			}
		} catch (e) {
			console.error(e)
			toast.error("Adjustment error")
		} finally {
			setAdjustLoading(false)
		}
	}

	const handleLogout = () => {
		setToken(null)
		localStorage.removeItem("sa_token")
		setLoginStep("credentials")
		setOtpForm({ email: "", code: "" })
		setOtpExpiresAt(null)
	}

	// Every screen change becomes a browser history entry, so Back/Forward step through the admin.
	useEffect(() => {
		const hash = formatAdminRoute({ tab: activeTab, company: companyRoute.uid, section: companyRoute.section, card: companyRoute.card, user: overviewModalUid ?? undefined })
		if (window.location.hash !== hash) window.history.pushState(null, "", hash)
	}, [activeTab, companyRoute, overviewModalUid])
	useEffect(() => {
		const onPop = () => {
			const route = parseAdminRoute(window.location.hash, (tab) => tab in ADMIN_TAB_META)
			setActiveTab(route.tab as AdminTab)
			setCompanyRoute({ uid: route.company ?? "", section: route.section, card: route.card })
			setOverviewModalUid(route.user ?? null)
		}
		window.addEventListener("popstate", onPop)
		// Plain <a href="#/..."> links (e.g. the card link in events) navigate by hash.
		window.addEventListener("hashchange", onPop)
		return () => { window.removeEventListener("popstate", onPop); window.removeEventListener("hashchange", onPop) }
	}, [])
	const openCompany = (uid: string, section?: string, card?: string) => {
		setCompanyRoute({ uid, section, card })
		setActiveTab("company")
	}
	const routeCompany = companies.find((item) => item.uid === companyRoute.uid) ?? null

	// Sidebar entries reset the filters a previous drill-down left behind.
	const openNavTab = (tab: AdminTab) => {
		if (tab === "company_admins") setSelectedCompanyForAdmins(null)
		if (tab === "users") setUsersCompanyFilter("")
		if (tab === "events") setEventsInitialFilters({})
		setActiveTab(tab)
	}

	const tabMeta = ADMIN_TAB_META[activeTab]
	const topupCompany = companies.find((company) => company.uid === topupForm.companyUid)
	const topupIssuerAccounts = getCompanyIssuerAccounts(topupCompany)
	const topupIssuerAccount = topupIssuerAccounts.find(
		(account) => account.issuer === topupForm.issuer
	)
	const companyStats = {
		total: companies.length,
		active: companies.filter((company) => company.status === "active").length,
		users: companies.reduce((total, company) => total + Number(company.users_count ?? 0), 0),
		cards: companies.reduce((total, company) => total + Number(company.cards_count ?? 0), 0)
	}
	const visibleCompanies = useMemo(
		() => filterAndSortCompanies(companies, companyDirectoryFilter),
		[companies, companyDirectoryFilter]
	)
	const hasCompanyDirectoryFilters = Boolean(
		companyDirectoryFilter.query.trim() ||
		companyDirectoryFilter.accountType !== DEFAULT_COMPANY_DIRECTORY_FILTER.accountType ||
		companyDirectoryFilter.status !== DEFAULT_COMPANY_DIRECTORY_FILTER.status ||
		companyDirectoryFilter.registrationSource !== DEFAULT_COMPANY_DIRECTORY_FILTER.registrationSource ||
		companyDirectoryFilter.providerCode !== DEFAULT_COMPANY_DIRECTORY_FILTER.providerCode ||
		companyDirectoryFilter.sort !== DEFAULT_COMPANY_DIRECTORY_FILTER.sort
	)
	const resetCompanyDirectoryFilters = () => {
		setCompanyDirectoryFilter({ ...DEFAULT_COMPANY_DIRECTORY_FILTER })
	}

	if (!token) {
		return (
																		<Box
				sx={{
					width: "100%",
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "24px",
					boxSizing: "border-box",
					background: `radial-gradient(60% 50% at 50% 0%, rgba(80,70,229,.10) 0%, transparent 70%), radial-gradient(40% 40% at 90% 100%, rgba(80,70,229,.06) 0%, transparent 70%), ${T.canvas}`,
					position: "fixed",
					top: 0,
					left: 0,
					zIndex: 9999
				}}
			>
				<VBox
					sx={{
						width: "min(400px, 100%)",
						padding: "32px",
						boxSizing: "border-box",
						backgroundColor: SA.surface,
						border: `1px solid ${SA.border}`,
						borderRadius: "16px",
						boxShadow: T.shadowLg,
						animation: `bo-pop .4s ${T.ease} both`,
						[SA_MOBILE]: { padding: "26px 22px", borderRadius: "16px" }
					}}
				>
					<HBox gap='10px' sx={{ alignItems: "center", marginBottom: "28px" }}>
						<AdminBrandMark />
						<VBox gap='0px'>
							<Typo sx={{ color: SA.text, fontSize: "0.9375rem", fontWeight: 650, letterSpacing: "-0.015em" }}>Cardpartner</Typo>
							<Typo sx={{ color: SA.muted, fontSize: "0.75rem" }}>Backoffice</Typo>
						</VBox>
					</HBox>
					<Typo sx={{ color: SA.text, fontSize: "1.375rem", lineHeight: 1.25, fontWeight: 650, letterSpacing: "-0.025em" }}>
						{loginStep === "credentials" ? "Super admin sign in" : "Check your email"}
					</Typo>
					<Spacer size='8px' />
					<Typo variant='text' sx={{ color: SA.muted, lineHeight: 1.5 }}>
						{loginStep === "credentials"
							? "Sign in to manage Cardpartner."
							: "Enter the confirmation code from your email"}
					</Typo>
					<Spacer size='28px' />
					{/* Enter submits the current step, like any login form. */}
					<div onKeyDown={(event) => {
						if (event.key !== "Enter" || loading || (event.target as HTMLElement).tagName === "BUTTON") return
						event.preventDefault()
						void (loginStep === "credentials" ? handleLogin() : handleVerifyLogin())
					}}>
					{loginStep === "credentials" ? (
						<>
							<Input
								data={loginForm}
								name='email'
								onChange={updateForm(setLoginForm)("email")}
								placeholder='admin@company.com'
								label='Email address'
							/>
							<Spacer size='16px' />
							<Input
								data={loginForm}
								name='password'
								onChange={updateForm(setLoginForm)("password")}
								placeholder='Enter password'
								label='Password'
								type='password'
							/>
							<Spacer size='24px' />
							<Button
								onClick={handleLogin}
								disabled={loading}
								size='medium'
								sx={{ width: "100%", minHeight: "46px", justifyContent: "center", backgroundColor: SA.accent, borderColor: SA.accent, borderRadius: "11px" }}
							>
								{loading ? "Sending code..." : "Continue"}
							</Button>
						</>
					) : (
						<>
							<Typo variant='caption' sx={{ color: SA.muted, lineHeight: 1.5 }}>
								Code sent to {otpForm.email || loginForm.email}
							</Typo>
							{otpExpiresAt && (
								<>
									<Spacer size='8px' />
									<Typo variant='caption' center sx={{ color: T.faint }}>
										Expires at {new Date(otpExpiresAt).toLocaleString()}
									</Typo>
								</>
							)}
							<Spacer size='20px' />
							<Input
								data={otpForm}
								name='code'
								onChange={updateForm(setOtpForm)("code")}
								placeholder='6-digit confirmation code'
								label='Confirmation code'
							/>
							<Spacer size='30px' />
							<Button
								onClick={handleVerifyLogin}
								disabled={loading}
								size='medium'
								sx={{ width: "100%", minHeight: "46px", justifyContent: "center", backgroundColor: SA.accent, borderColor: SA.accent, borderRadius: "11px" }}
							>
								{loading ? "Verifying..." : "Verify Code"}
							</Button>
							<Spacer size='12px' />
							<Button
								variant='secondary'
								onClick={() => {
									setLoginStep("credentials")
									setOtpForm({ email: loginForm.email, code: "" })
									setOtpExpiresAt(null)
								}}
								disabled={loading}
								sx={{ width: "100%", justifyContent: "center" }}
							>
								Back
							</Button>
						</>
					)}
					</div>
				</VBox>
			</Box>
		)
	}

	return (
		<>
			<Box
				sx={{
					width: "100%",
					height: "100vh",
					backgroundColor: SA.canvas,
					position: "fixed",
					top: 0,
					left: 0,
					zIndex: 9999,
					display: "flex"
				}}
			>
				{/* Sidebar */}
				<VBox
					sx={{
						width: "244px",
						minWidth: "244px",
						height: "100%",
						boxSizing: "border-box",
						background: "linear-gradient(180deg, #17171F 0%, #121218 100%)",
						padding: "14px 10px",
						justifyContent: "space-between",
						borderRight: "1px solid #24242E",
						[SA_COMPACT]: { width: "64px", minWidth: "64px", padding: "14px 8px" }
					}}
				>
					<VBox sx={{ minHeight: 0, overflowY: "auto", margin: "0 -4px", padding: "0 4px" }}>
						<HBox gap='10px' sx={{ alignItems: "center", padding: "4px 8px 18px", [SA_COMPACT]: { justifyContent: "center", padding: "4px 0 18px" } }}>
							<AdminBrandMark />
							<VBox gap='0px' sx={{ [SA_COMPACT]: { display: "none" } }}>
								<Typo sx={{ color: "#FFFFFF", fontSize: "0.875rem", fontWeight: 650, letterSpacing: "-0.015em", lineHeight: 1.25 }}>Cardpartner</Typo>
								<Typo sx={{ color: "rgba(255,255,255,.5)", fontSize: "0.75rem", lineHeight: 1.3 }}>Backoffice</Typo>
							</VBox>
						</HBox>

						{ADMIN_NAV.map((group) => <Fragment key={group.label}>
							<SidebarSectionLabel>{group.label}</SidebarSectionLabel>
							<VBox gap='1px'>
								{group.items.map((item) => <SidebarItem
									key={item.tab}
									label={item.label}
									active={activeTab === item.tab || (item.tab === "companies" && activeTab === "company")}
									onClick={() => openNavTab(item.tab)}
									icon={<AdminIcon name={item.icon} size={16} />}
								/>)}
							</VBox>
						</Fragment>)}
					</VBox>

					<Box sx={{ paddingTop: "10px", borderTop: "1px solid rgba(255,255,255,.08)" }}>
						<SidebarItem label='Log out' active={false} onClick={handleLogout} icon={<AdminIcon name='logout' size={16} />} />
					</Box>
				</VBox>

				{/* Main Content */}
				<VBox
					sx={{
						flex: 1,
						minWidth: 0,
						boxSizing: "border-box",
						padding: "28px 40px 56px",
						overflowX: "hidden",
						overflowY: "auto",
						backgroundColor: SA.canvas,
						[SA_COMPACT]: { padding: "22px 22px 40px" },
						[SA_MOBILE]: { padding: "18px 14px 32px" }
					}}
				>
					<Box key={activeTab} sx={{ maxWidth: "1320px", width: "100%", margin: "0 auto", animation: `bo-rise .3s ${T.ease} both` }}>
						{activeTab !== "company" && <VBox gap='4px' sx={{ minWidth: 0, marginBottom: "24px", [SA_MOBILE]: { marginBottom: "18px" } }}>
							<Typo sx={{ color: T.muted, fontSize: "0.8125rem", fontWeight: 500 }}>{tabMeta.eyebrow}</Typo>
							<Typo sx={{ margin: 0, color: T.text, fontSize: "1.625rem", lineHeight: 1.2, fontWeight: 650, letterSpacing: "-0.03em", [SA_MOBILE]: { fontSize: "1.375rem" } }}>{tabMeta.title}</Typo>
							<Typo sx={{ color: T.muted, fontSize: "0.875rem", lineHeight: 1.5, maxWidth: "680px" }}>{tabMeta.description}</Typo>
						</VBox>}

						{activeTab === "companies" && (
							<VBox gap='20px'>
								<Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px", [SA_COMPACT]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, [SA_MOBILE]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
									<AdminMetric label='Clients' value={String(companyStats.total)} icon='companies' />
									<AdminMetric label='Active' value={String(companyStats.active)} icon='check' tone='success' />
									<AdminMetric label='Users' value={String(companyStats.users)} icon='user' />
									<AdminMetric label='Cards' value={String(companyStats.cards)} icon='card' tone='money' />
								</Box>
								<KitPanel
									title='Company directory'
									hint='Company and private accounts. Click a row to open the client.'
									actions={<>
										<Button onClick={fetchCompanies} variant='secondarySmall' icon={<AdminIcon name='refresh' size={15} />}>Refresh</Button>
										<Button onClick={() => setActiveTab("create_private")} variant='secondarySmall' icon={<AdminIcon name='plus' size={15} />}>Private account</Button>
										<Button onClick={() => setActiveTab("create_company")} variant='primarySmall' icon={<AdminIcon name='plus' size={15} />}>Create company</Button>
									</>}
								>
									<VBox gap='12px' sx={{ marginBottom: "18px" }}>
										<SearchField
											label='Find a client by email, name or UID'
											placeholder='Email, name, UID or VAT'
											value={companyDirectoryFilter.query}
											onChange={(query) => {
												setCompanyDirectoryFilter((previous) => ({ ...previous, query }))
											}}
										/>
										<FilterBar>
											<label>Account type<select value={companyDirectoryFilter.accountType} onChange={(event) => { setCompanyDirectoryFilter((previous) => ({ ...previous, accountType: event.target.value as CompanyAccountTypeFilter })) }}>{COMPANY_ACCOUNT_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
											<label>Status<select value={companyDirectoryFilter.status} onChange={(event) => { setCompanyDirectoryFilter((previous) => ({ ...previous, status: event.target.value as CompanyStatusFilter })) }}>{COMPANY_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
											<label>Source<select value={companyDirectoryFilter.registrationSource} onChange={(event) => { setCompanyDirectoryFilter((previous) => ({ ...previous, registrationSource: event.target.value as CompanyRegistrationSourceFilter })) }}>{COMPANY_REGISTRATION_SOURCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
											<label>Issuer<select value={companyDirectoryFilter.providerCode} onChange={(event) => { setCompanyDirectoryFilter((previous) => ({ ...previous, providerCode: event.target.value })) }}>{COMPANY_PROVIDER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
											<label>Sort by<select value={companyDirectoryFilter.sort} onChange={(event) => { setCompanyDirectoryFilter((previous) => ({ ...previous, sort: event.target.value as CompanyDirectorySort })) }}>{COMPANY_SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
											<HBox gap='10px' sx={{ marginLeft: "auto", alignItems: "center", alignSelf: "flex-end", minHeight: "34px" }}>
												{hasCompanyDirectoryFilters && <Button variant='secondarySmall' onClick={resetCompanyDirectoryFilters}>Reset</Button>}
												<Box role='status' aria-live='polite'><Typo sx={{ color: SA.muted, fontSize: "0.8125rem", whiteSpace: "nowrap" }}>Showing {visibleCompanies.length} of {companies.length} accounts</Typo></Box>
											</HBox>
										</FilterBar>
									</VBox>
									{visibleCompanies.length === 0 ? (
										<VBox center gap='6px' sx={{ padding: "48px 20px", border: `1px dashed ${T.border}`, borderRadius: "10px", backgroundColor: T.surface2, marginTop: "8px" }}>
											<Typo sx={{ color: SA.text, fontWeight: 600 }}>No clients found</Typo>
											<Typo sx={{ color: SA.muted, fontSize: "0.8125rem" }}>Try another email, name or UID, or clear the filters.</Typo>
											{hasCompanyDirectoryFilters && <Button variant='secondarySmall' onClick={resetCompanyDirectoryFilters} sx={{ marginTop: "8px" }}>Clear filters</Button>}
										</VBox>
									) : (
									<>
										<Paged rows={visibleCompanies}>{(pageRows) => <>
										<KitTable>
											<thead><tr><th>Client</th><th>UID</th><th>Users</th><th>Admins</th><th>Issuers</th><th>Created</th><th>Status</th><th /></tr></thead>
											<tbody>{pageRows.map((row) => <tr key={row.uid} {...linkRow(() => openCompany(row.uid))}>
												<td><HBox gap='10px' sx={{ alignItems: "center" }}><Avatar name={row.name} /><Box sx={{ minWidth: 0 }}><span style={{ fontWeight: 600 }}>{row.name}</span>{row.type === "private" && <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 550, color: T.accentText, background: T.accentSoft, padding: "1px 7px", borderRadius: 999 }}>Private</span>}<small>{row.admins?.[0]?.email ?? ""}</small></Box></HBox></td>
												<td><code style={{ color: T.text2 }}>{row.uid}</code>{(row.number || row.vat_number) && <small>{[row.number && `Reg ${row.number}`, row.vat_number && `VAT ${row.vat_number}`].filter(Boolean).join(" · ")}</small>}</td>
												<td>{Number(row.users_count ?? 0)}</td>
												<td>{Number(row.admins_count ?? row.admins?.length ?? 0)}</td>
												<td>{row.issuers?.length ? row.issuers.map((issuer) => <div key={issuer.issuer} style={{ whiteSpace: "nowrap" }}>{issuer.issuer_label || issuer.provider_code}{issuer.is_default && row.issuers!.length > 1 && <span style={{ color: T.muted }}> · default</span>}</div>) : <span style={{ color: SA.muted }}>Not connected</span>}</td>
												<td style={{ color: T.text2 }}>{new Date(row.created_at).toLocaleDateString()}</td>
												<td><StatusBadge value={row.status} /></td>
												<OpenCell />
											</tr>)}</tbody>
										</KitTable>
										</>}</Paged>
									</>
									)}
								</KitPanel>
							</VBox>
						)}

						{activeTab === "company_admins" && (
							<VBox gap='20px'>
								{!selectedCompanyForAdmins ? (
									<ClientPicker companies={companies} title='Choose a client' hint='Shows the admins of one client.' onPick={(comp) => { setSelectedCompanyForAdmins(comp); fetchAdmins(comp.uid) }} />
								) : (
									<VBox gap='20px'>
										<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<Button
												variant='secondarySmall'
												onClick={() => setSelectedCompanyForAdmins(null)}
											>
												← Choose another client
											</Button>
											<PanelTitle>Admins for {selectedCompanyForAdmins.name}</PanelTitle>
										</Box>

										{/* List of Admins */}
									<Tile
										sx={{
											...SA_PANEL_SX,
											padding: "0px",
												overflow: "hidden",
												border: `1px solid ${SA.border}`,
												boxShadow: SA.shadow
											}}
										>
											<Box
												sx={{
													padding: "24px",
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													borderBottom: `1px solid ${T.border}`,
													background:
														"linear-gradient(to right, rgba(255,255,255,0.5), rgba(255,255,255,0))"
												}}
											>
												<VBox gap='4px'>
													<PanelTitle>Admins</PanelTitle>
													<Typo sx={{ color: T.muted, fontSize: "0.8125rem", lineHeight: 1.5 }}>
														People who can sign in to {selectedCompanyForAdmins.name}.
													</Typo>
												</VBox>
												<Button
													onClick={() => {
														setCreateAdminForm((prev) => ({
															...prev,
															company_uid: selectedCompanyForAdmins.uid
														}))
														setActiveTab("create_admin")
													}}
													variant='primarySmall'
													size='medium'
												>
													New admin
												</Button>
											</Box>
											<Box sx={{ overflowX: "auto", padding: "6px 20px 10px" }}>
												<Box sx={{ minWidth: "800px" }}>
													<Paged rows={admins}>{(pageRows) => <KitTable>
														<thead><tr><th>Admin</th><th>UID</th><th>Status</th><th /></tr></thead>
														<tbody>{pageRows.map((row) => <tr key={row.uid}>
															<td><span style={{ fontWeight: 600 }}>{row.name}</span><small>{row.email}</small></td>
															<td><code style={{ color: T.text2 }}>{row.uid}</code></td>
															<td><StatusBadge value={row.status} /></td>
															<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
																<Button variant='secondarySmall' size='small' onClick={() => {
																	const companyUid = selectedCompanyForAdmins!.uid
																	void askValue({ title: `New password for ${row.name}`, body: "The admin signs in with this password from now on. Share it over a secure channel.", confirmLabel: "Change password", input: { label: "New password", type: "password", minLength: 8 } })
																		.then((newPass) => { if (newPass) handleChangeAdminPassword(row.uid, companyUid, newPass) })
																}}>Change password</Button>{" "}
																{row.status === "disabled"
																	? <Button variant='secondarySmall' size='small' onClick={() => handleAdminStateChange(row.uid, selectedCompanyForAdmins!.uid, "enable")}>Enable</Button>
																	: <Button variant='secondarySmall' size='small' sx={{ color: T.danger, borderColor: T.dangerLine }} onClick={() => {
																		const companyUid = selectedCompanyForAdmins!.uid
																		void askConfirm({ title: `Disable ${row.name}?`, body: "They will not be able to sign in until you enable them again.", confirmLabel: "Disable admin", danger: true })
																			.then((ok) => { if (ok) handleAdminStateChange(row.uid, companyUid, "disable") })
																	}}>Disable</Button>}
															</td>
														</tr>)}</tbody>
													</KitTable>}</Paged>
												</Box>
											</Box>
										</Tile>
									</VBox>
								)}
							</VBox>
						)}

						{activeTab === "create_company" && (
							<VBox gap='20px'>
								<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
									<Button
										variant='secondarySmall'
										onClick={() => setActiveTab("companies")}
									>
										← Back to clients
									</Button>
								</Box>
								<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
									<VBox gap='20px'>
										<Input
											data={createCompanyForm}
											name='name'
											onChange={updateForm(setCreateCompanyForm)("name")}
											placeholder='e.g. Acme Corp'
											label='Company name'
										/>
										<Input
											data={createCompanyForm}
											name='number'
											onChange={updateForm(setCreateCompanyForm)("number")}
											placeholder='e.g. 12345678'
											label='Registration number'
										/>
										<Input
											data={createCompanyForm}
											name='vat_number'
											onChange={updateForm(setCreateCompanyForm)("vat_number")}
											placeholder='e.g. US123456789'
											label='VAT number'
										/>
										<Select
											name='type'
											values={[
												{ name: "Company (default)", value: "company" },
												{ name: "Private", value: "private" }
											]}
											onChange={(v) => {
												// Select unwraps {name,value} options to the raw value string
														const next = (
															typeof v === "object" && v && "value" in v ? (v as { value: unknown }).value : v
														) as "company" | "private"
												updateForm(setCreateCompanyForm)("type")(next ?? "company")
											}}
											value={{
												name:
													createCompanyForm.type === "private"
														? "Private"
														: "Company (default)",
												value: createCompanyForm.type
											}}
											label='Account type'
										/>
										<Spacer size='10px' />
										<Button
											onClick={handleCreateCompany}
											disabled={loading}
											size='medium'
										>
											{loading ? "Creating…" : "Create company"}
										</Button>
									</VBox>
								</Tile>
							</VBox>
						)}

						{activeTab === "create_private" && (
							<VBox gap='20px'>
								<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
									<Button
										variant='secondarySmall'
										onClick={() => setActiveTab("companies")}
									>
										← Back to clients
									</Button>
								</Box>
								<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
									<VBox gap='20px'>
										<Typo sx={{ color: T.muted, fontSize: "0.8125rem", lineHeight: 1.5 }}>
											Creates a private company together with its admin in a single call.
											The admin email must be unique.
										</Typo>
										<Input
											data={createPrivateForm}
											name='name'
											onChange={updateForm(setCreatePrivateForm)("name")}
											placeholder='e.g. Doe Family'
											label='Company name'
										/>
										<Input
											data={createPrivateForm}
											name='number'
											onChange={updateForm(setCreatePrivateForm)("number")}
											placeholder='e.g. REG-PRIV-1'
											label='Registration number'
										/>
										<Input
											data={createPrivateForm}
											name='vat_number'
											onChange={updateForm(setCreatePrivateForm)("vat_number")}
											placeholder='optional'
											label='VAT number'
										/>
										<Spacer size='4px' />
										<Typo sx={{ color: T.text, fontSize: "0.875rem", fontWeight: 600, marginTop: "6px" }}>Admin account</Typo>
										<Input
											data={createPrivateForm}
											name='admin_name'
											onChange={updateForm(setCreatePrivateForm)("admin_name")}
											placeholder='e.g. John Doe'
											label='Admin name'
										/>
										<Input
											data={createPrivateForm}
											name='admin_email'
											autoComplete='off'
											onChange={updateForm(setCreatePrivateForm)("admin_email")}
											placeholder='john@doe.family'
											label='Admin email'
										/>
										<Input
											data={createPrivateForm}
											name='admin_password'
											autoComplete='new-password'
											onChange={updateForm(setCreatePrivateForm)("admin_password")}
											placeholder='At least 8 characters'
											label='Admin password'
											type='password'
										/>
										<Spacer size='10px' />
										<Button
											onClick={handleCreatePrivate}
											disabled={loading}
											size='medium'
										>
											{loading ? "Creating…" : "Create private account"}
										</Button>
									</VBox>
								</Tile>
							</VBox>
						)}

						{activeTab === "create_admin" && (
							<VBox gap='20px'>
								<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
									<Button
										variant='secondarySmall'
										onClick={() => setActiveTab("company_admins")}
									>
										← Back to admins
									</Button>
								</Box>
								<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
									<VBox gap='20px'>
										<Select
											name='company_uid'
											values={companies.map((c) => ({
												name: c.name + " (" + c.uid + ")",
												value: c.uid
											}))}
											onChange={(v) =>
												updateForm(setCreateAdminForm)("company_uid")(v ?? "")
											}
											value={
												createAdminForm.company_uid
													? {
															name:
																companies.find(
																	(c) => c.uid === createAdminForm.company_uid
																)?.name || createAdminForm.company_uid,
															value: createAdminForm.company_uid
														}
													: null
											}
											label='Company'
										/>
										<Input
											data={createAdminForm}
											name='name'
											onChange={updateForm(setCreateAdminForm)("name")}
											placeholder='Full name'
											label='Admin name'
										/>
										<Input
											data={createAdminForm}
											name='email'
											autoComplete='off'
											onChange={updateForm(setCreateAdminForm)("email")}
											placeholder='email@example.com'
											label='Email'
										/>
										<Input
											data={createAdminForm}
											name='password'
											autoComplete='new-password'
											onChange={updateForm(setCreateAdminForm)("password")}
											placeholder='At least 8 characters'
											label='Password'
											type='password'
										/>
										<Spacer size='10px' />
										<Button onClick={handleCreateAdmin} disabled={loading} size='medium'>
											{loading ? "Creating…" : "Create admin"}
										</Button>
									</VBox>
								</Tile>
							</VBox>
						)}

						{activeTab === "topup" && (
							<VBox gap='20px'>
								<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
									<Button
										variant='secondarySmall'
										onClick={() => setActiveTab("companies")}
									>
										← Back
									</Button>
								</Box>
								<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
									<VBox gap='20px'>
										<Select
											name='companyUid'
											values={companies.map((c) => ({
												name: c.name + " (" + c.uid + ")",
												value: c.uid
											}))}
										onChange={(v) => {
											const company = companies.find((item) => item.uid === v)
											const account = getDefaultIssuerAccount(company)
											setTopupForm((current) => ({
												...current,
												companyUid: v ?? "",
												issuer: account?.issuer ?? null,
												currency: account?.currency ?? "USD"
											}))
										}}
											value={
												topupForm.companyUid
													? {
															name:
																companies.find((c) => c.uid === topupForm.companyUid)
																	?.name || topupForm.companyUid,
															value: topupForm.companyUid
														}
													: null
											}
										label='Company'
									/>
									<Select
										name='issuer'
										values={topupIssuerAccounts.map((account) => ({
											name: getIssuerAccountLabel(account),
											value: String(account.issuer)
										}))}
										onChange={(value) => {
											const issuer = Number(value)
											const account = topupIssuerAccounts.find(
												(item) => item.issuer === issuer
											)
											setTopupForm((current) => ({
												...current,
												issuer: account?.issuer ?? null,
												currency: account?.currency ?? "USD"
											}))
										}}
										value={
											topupIssuerAccount
												? {
													name: getIssuerAccountLabel(topupIssuerAccount),
													value: String(topupIssuerAccount.issuer)
												}
												: null
										}
										label='Issuer account'
										disabled={!topupForm.companyUid || topupIssuerAccounts.length === 0}
									/>
									{topupForm.companyUid && topupIssuerAccounts.length === 0 && (
										<Typo sx={{ color: SA.danger, fontSize: "0.78rem" }}>
											This company has no issuer accounts. Assign a provider first.
										</Typo>
									)}
										<Input
										data={{ amount: topupForm.amount }}
											name='amount'
											onChange={updateForm(setTopupForm)("amount")}
											placeholder='0.00'
											type='number'
											label='Amount'
										/>
										<Input
										data={{ currency: topupForm.currency }}
											name='currency'
											onChange={updateForm(setTopupForm)("currency")}
										placeholder='USD'
										label='Currency'
										readOnly
										/>
										<Spacer size='10px' />
									<Button onClick={handleTopup} disabled={loading || topupForm.issuer === null} size='medium'>
											{loading ? "Processing…" : "Add funds"}
										</Button>
									</VBox>
								</Tile>
							</VBox>
						)}

						{activeTab === "partner_tokens" && (
							<Tile
								sx={{
									...SA_PANEL_SX,
									padding: "0px",
									overflow: "hidden",
									border: `1px solid ${SA.border}`,
									boxShadow: SA.shadow
								}}
							>
								<Box
									sx={{
										padding: "20px",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										borderBottom: `1px solid ${T.border}`
									}}
								>
									<PanelTitle>All tokens</PanelTitle>
									<Box sx={{ display: "flex", gap: "10px" }}>
										<Button onClick={fetchPartnerTokens} variant='secondarySmall'>
											Refresh
										</Button>
										<Button
											onClick={() => setActiveTab("create_partner_token")}
											variant='primarySmall'
										>
											Create token
										</Button>
									</Box>
								</Box>
								<Box sx={{ overflowX: "auto", padding: "6px 20px 10px" }}>
									<Box sx={{ minWidth: "940px", width: "100%" }}>
										<Paged rows={partnerTokens}>{(pageRows) => <KitTable>
											<thead><tr><th>Partner</th><th>Key ID</th><th>Secret</th><th>Status</th><th /></tr></thead>
											<tbody>{pageRows.map((row) => <tr key={row.kid}>
												<td><span style={{ fontWeight: 600 }}>{row.partner_name}</span><small>{row.user_uid}</small></td>
												<td><code title='Click to copy' style={{ cursor: "copy" }} onClick={() => { navigator.clipboard.writeText(row.kid); toast.success("Key ID copied") }}>{row.kid}</code></td>
												<td><code title='Click to copy' style={{ cursor: "copy", display: "inline-block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }} onClick={() => { navigator.clipboard.writeText(row.secret_key); toast.success("Secret copied") }}>{row.secret_key}</code></td>
												<td><StatusBadge value={row.status} /></td>
												<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
													{row.status === "disabled"
														? <Button variant='secondarySmall' size='small' onClick={() => handlePartnerTokenAction(row.kid, "enable")}>Enable</Button>
														: <Button variant='secondarySmall' size='small' sx={{ color: T.danger, borderColor: T.dangerLine }} onClick={() => {
															void askConfirm({ title: `Disable token for ${row.partner_name}?`, body: "Requests with this key stop working until you enable it again.", confirmLabel: "Disable token", danger: true })
																.then((ok) => { if (ok) handlePartnerTokenAction(row.kid, "disable") })
														}}>Disable</Button>}{" "}
													<Button variant='secondarySmall' size='small' sx={{ color: T.danger, borderColor: T.dangerLine }} onClick={() => {
														void askConfirm({ title: `Delete token for ${row.partner_name}?`, body: "This cannot be undone. The partner needs a new token to keep working.", confirmLabel: "Delete token", danger: true })
															.then((ok) => { if (ok) handlePartnerTokenAction(row.kid, "delete") })
													}}>Delete</Button>
												</td>
											</tr>)}</tbody>
										</KitTable>}</Paged>
									</Box>
								</Box>
							</Tile>
						)}

						{activeTab === "create_partner_token" && (
							<VBox gap='20px'>
								<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
									<Button
										variant='secondarySmall'
										onClick={() => setActiveTab("partner_tokens")}
									>
										← Back to tokens
									</Button>
								</Box>
								<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
									<VBox gap='20px'>
										<Input
											data={createPartnerTokenForm}
											name='partner_name'
											onChange={updateForm(setCreatePartnerTokenForm)("partner_name")}
											placeholder='e.g. My Partner'
											label='Partner name'
										/>
										<Fields style={{ gridTemplateColumns: "1fr" }}>
											<label>User<EntityInput token={token ?? ""} kind='user' name='user_uid' value={createPartnerTokenForm.user_uid} onChange={updateForm(setCreatePartnerTokenForm)("user_uid")} /></label>
										</Fields>
										<Input
											data={createPartnerTokenForm}
											name='expires_at'
											onChange={updateForm(setCreatePartnerTokenForm)("expires_at")}
											type='date'
											label='Expires on (optional)'
										/>
										<Spacer size='10px' />
										<Button
											onClick={handleCreatePartnerToken}
											disabled={loading}
											size='medium'
										>
											{loading ? "Creating…" : "Create token"}
										</Button>
									</VBox>
								</Tile>
							</VBox>
						)}

						{activeTab === "users" && token && (
							<SuperAdminUserDirectory
								token={token}
								companies={companies}
								initialCompanyUid={usersCompanyFilter}
								onAdjust={openBuyerAdjustPopup}
								onShowAllEvents={(uid) => { setEventsInitialFilters({ user: uid }); setActiveTab("events") }}
								onOpenCompany={(uid) => openCompany(uid)}
							/>
						)}

						{activeTab === "cards" && (
							<Tile
								sx={{
									...SA_PANEL_SX,
									padding: "0px",
									overflow: "hidden",
									border: `1px solid ${SA.border}`,
									boxShadow: SA.shadow
								}}
							>
								<FilterBar style={{ padding: "16px 20px", borderBottom: `1px solid ${SA.border}`, background: T.surface2 }}>
									<label>Client<select value={cardsCompanyFilter} onChange={(event) => {
										const uid = event.target.value
										setCardsCompanyFilter(uid)
										setCardsUserFilter("")
										void fetchUsers(uid || undefined)
										fetchCards(uid || undefined, undefined)
									}}><option value=''>All clients</option>{companies.map((c) => <option key={c.uid} value={c.uid}>{c.name}</option>)}</select></label>
									<label>Owner<select value={cardsUserFilter} onChange={(event) => {
										const uid = event.target.value
										setCardsUserFilter(uid)
										fetchCards(cardsCompanyFilter || undefined, uid || undefined)
									}}><option value=''>All owners</option>{users.filter((u) => !cardsCompanyFilter || u.company_uid === cardsCompanyFilter).map((u) => <option key={u.uid} value={u.uid}>{u.name || u.email}</option>)}</select></label>
									<label style={{ flex: "1 1 240px" }}>Search cards<input type='search' value={cardsUidQuery} placeholder='Card number, UID or owner' onChange={(event) => setCardsUidQuery(event.target.value)} style={{ width: "100%" }} /></label>
									<label>Status<select value={cardsStatusFilter} onChange={(event) => { const status = event.target.value; setCardsStatusFilter(status); void fetchCards(cardsCompanyFilter || undefined, cardsUserFilter || undefined, cardsUidQuery, status) }}><option value=''>All statuses</option>{["created", "active", "closed", "blocked", "lost", "replaced", "unavailable", "error", "declined", "frozen"].map((status) => <option key={status} value={status}>{status[0].toUpperCase() + status.slice(1)}</option>)}</select></label>
									<label>Sort<select value={cardsSort} onChange={(event) => { const sort = event.target.value as "" | "balance"; cardsSortRef.current = sort; setCardsSort(sort); void fetchCards(cardsCompanyFilter || undefined, cardsUserFilter || undefined, cardsUidQuery, cardsStatusFilter) }}><option value=''>Newest first</option><option value='balance'>Balance, highest first</option></select></label>
								</FilterBar>
								<Box sx={{ overflowX: "auto", padding: "8px 20px 4px" }}>
									<KitTable>
										<thead><tr><th>Card</th><th>Owner</th><th>Client</th><th>Issuer</th><th>Balance</th><th>Status</th><th /></tr></thead>
										<tbody>{cards.map((row) => {
											const company = companies.find((item) => item.uid === row.company_uid)
											return <tr key={row.uid} {...linkRow(() => openCompany(row.company_uid, undefined, row.uid))}>
												<td><span style={{ fontWeight: 600 }}>{row.mask || (row.last4 ? `•••• ${row.last4}` : "—")}</span><small>{row.uid}</small></td>
												<td>{users.find((u) => u.uid === row.user_uid)?.name ?? row.user_uid}</td>
												<td>{company?.name ?? row.company_uid}</td>
												<td>{company?.issuers?.find((item) => item.issuer === row.issuer)?.issuer_label ?? (Number.isInteger(row.issuer) ? `Issuer ${row.issuer}` : "—")}</td>
												<td>{formatCurrencyWithNumberToSymbolWithNumber(Number(row.balance), row.currency || DEFAULT_CURRENCY)}</td>
												<td><StatusBadge value={cardDisplayStatus(row)} />{row.blocked_at && <small>{date(row.blocked_at)}</small>}</td>
												<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
													{row.provider_code === "wasabi" && <span onClick={(event) => event.stopPropagation()}><Button variant='secondarySmall' size='small' title='Simulate sandbox spend' onClick={() => setSpendCard(row)}>Sandbox spend</Button></span>}
													<span data-open style={{ display: "inline-block", color: T.faint, fontSize: 18, lineHeight: 1, marginLeft: 10, verticalAlign: "middle", transition: "color .14s, transform .14s" }}>›</span>
												</td>
											</tr>
										})}</tbody>
									</KitTable>
								</Box>
								<div style={{ padding: "0 20px 14px" }}><Pager total={cardsTotal} from={cardsOffset + 1} to={cardsOffset + cards.length} page={Math.floor(cardsOffset / 50)} pages={Math.max(1, Math.ceil(cardsTotal / 50))} onPage={(next) => { if (!loading) void fetchCards(cardsCompanyFilter || undefined, cardsUserFilter || undefined, cardsUidQuery, cardsStatusFilter, next * 50) }} /></div>
							</Tile>
						)}

						{activeTab === "provider_balances" && token && <SuperAdminIssuerFunds token={token} onOpenCompany={(uid) => openCompany(uid)} />}

						{activeTab === "balances" && token && (
							<SuperAdminBalanceReconciliation token={token} companies={companies} />
						)}

						{activeTab === "commissions" && (
							<VBox gap='20px'>
								{!commissionsCompany ? (
									<ClientPicker companies={companies} title='Choose a client' hint='Rates are set per client and issuer. They are also on the client page, tab Our rates.' onPick={(comp) => { setCommissionsCompany(comp); fetchCommissions(comp.uid) }} />
								) : (
									<VBox gap='20px'>
										<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
											<Button
												variant='secondarySmall'
												onClick={() => {
													setCommissionsCompany(null)
													setCommissions([])
												}}
											>
												← Choose another client
											</Button>
											<PanelTitle>Our rates for {commissionsCompany.name}</PanelTitle>
										</Box>

										{/* Commission rows list */}
										<Tile
											sx={{
												...SA_PANEL_SX,
												padding: "0px",
												overflow: "hidden",
											border: `1px solid ${SA.border}`,
											boxShadow: SA.shadow
											}}
										>
											<Box
												sx={{
													padding: "20px",
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													borderBottom: `1px solid ${T.border}`
												}}
											>
												<PanelTitle>Rates</PanelTitle>
												<Button
													onClick={() => fetchCommissions(commissionsCompany.uid)}
													variant='secondarySmall'
													disabled={commissionLoading}
												>
													Refresh
												</Button>
											</Box>
											<Box sx={{ overflowX: "auto", padding: "6px 20px 10px" }}>
												<Box sx={{ minWidth: "700px" }}>
													<Paged rows={commissions}>{(pageRows) => <KitTable>
														<thead><tr><th>Issuer</th><th>Operation</th><th>Our percent</th><th>Source</th><th /></tr></thead>
														<tbody>{pageRows.map((row) => <tr key={`${row.provider_code}:${row.type}`}>
															<td style={{ fontWeight: 600 }}>{providerName(row.provider_code)}</td>
															<td>{sentence(row.type)}</td>
															<td>{(row.our_percent / MINOR_UNIT_FACTOR).toFixed(PERCENT_DECIMAL_PLACES)}%</td>
															<td>{row.source === "default" ? "Default" : "Set for this company"}</td>
															<td style={{ textAlign: "right" }}>
																<Button variant='secondarySmall' size='small' sx={{ color: T.danger, borderColor: T.dangerLine }} disabled={commissionLoading || row.id === null} onClick={() => {
																	void askConfirm({ title: "Delete this rate?", body: `${providerName(row.provider_code)} · ${sentence(row.type)}. The client falls back to the default rate.`, confirmLabel: "Delete rate", danger: true })
																		.then((ok) => { if (ok) handleDeleteCommission(row.id) })
																}}>Delete</Button>
															</td>
														</tr>)}</tbody>
													</KitTable>}</Paged>
												</Box>
											</Box>
										</Tile>

										{/* Add / upsert commission form */}
										<Tile sx={{ ...SA_PANEL_SX, maxWidth: "600px" }}>
											<VBox gap='20px'>
												<PanelTitle>Set a rate</PanelTitle>
												<Typo sx={{ color: T.muted, fontSize: "0.8125rem", lineHeight: 1.5 }}>
													Only our markup percentage is stored here. The provider's fee is
													fetched live. Percents are entered as decimals (e.g. 1.05 =
													1.05%).
												</Typo>
												<Select
													name='commission_provider_code'
													values={COMMISSION_PROVIDERS.map((code) => ({ name: providerName(code), value: code }))}
													onChange={(v) =>
														updateForm(setCommissionForm)("provider_code")(
															(v as unknown as string) || "cardspro"
														)
													}
													value={{
														name: providerName(commissionForm.provider_code),
														value: commissionForm.provider_code
													}}
													label='Issuer'
												/>
												<Select
													name='commission_type'
													values={COMMISSION_TYPES}
													onChange={(v) =>
														updateForm(setCommissionForm)("type")(
															(v as unknown as string) || "topup"
														)
													}
													value={{
														name: COMMISSION_TYPES.find((item) => item.value === commissionForm.type)?.name ?? commissionForm.type,
														value: commissionForm.type
													}}
													label='Operation'
												/>
												<Input
													data={commissionForm}
													name='our_percent'
													onChange={updateForm(setCommissionForm)("our_percent")}
													placeholder='e.g. 1.05'
													type='number'
													label='Our percent (1.05 = 1.05%)'
												/>
												<Button
													onClick={handleSetCommission}
													disabled={commissionLoading}
													size='medium'
												>
													{commissionLoading ? "Saving…" : "Save rate"}
												</Button>
											</VBox>
										</Tile>
									</VBox>
								)}
							</VBox>
						)}

						{activeTab === "providers" && (
							<VBox gap='20px'>
								{!providersCompany ? (
									<ClientPicker companies={companies} title='Choose a client' hint='Turn issuers on or off and set their order for one client.' onPick={(comp) => { setProvidersCompany(comp); fetchProviderMatrix(comp.uid) }} />
								) : (
									<VBox gap='20px'>
										<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
											<Button
												variant='secondarySmall'
												onClick={() => {
													setProvidersCompany(null)
													setProviderMatrix([])
												}}
											>
												← Choose another client
											</Button>
											<PanelTitle>Issuer access · {providersCompany.name}</PanelTitle>
										</Box>

										<Tile
											sx={{
												...SA_PANEL_SX,
												padding: "0px",
												overflow: "hidden",
											border: `1px solid ${SA.border}`,
											boxShadow: SA.shadow
											}}
										>
											<Box
												sx={{
													padding: "20px",
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													borderBottom: `1px solid ${T.border}`
												}}
											>
												<VBox gap='3px'><PanelTitle>Issuers for this client</PanelTitle><Typo sx={{ color: T.muted, fontSize: "0.8125rem" }}>Enabled issuers can issue cards for this client. Lower order is tried first.</Typo></VBox>
												<Button
													onClick={() => fetchProviderMatrix(providersCompany.uid)}
													variant='secondarySmall'
													disabled={providersLoading}
												>
													Refresh
												</Button>
											</Box>
											<Box sx={{ overflowX: "auto", padding: "6px 20px 10px" }}>
												<Box sx={{ minWidth: "600px" }}>
													{PROVIDERS.map(({ code, name, description }) => {
														const isActive = providerMatrix.some(
															(r) =>
																r &&
																(r.provider_code === code || r.code === code) &&
																(!r.company_uid ||
																	r.company_uid === providersCompany.uid) &&
																r.status === "active"
														)
														return (
															<Box
																key={code}
																sx={{
																	padding: "14px 4px",
																	display: "flex",
																	justifyContent: "space-between",
																	alignItems: "center",
																	borderBottom: `1px solid ${T.border}`,
																	"&:last-child": { borderBottom: 0 },
																	gap: "10px"
																}}
															>
																<VBox gap='2px'>
																	<Box sx={{ fontWeight: 600 }}>{name}</Box>
																	{description && (
																		<Typo sx={{ color: T.muted, fontSize: "0.8125rem" }}>
																			{description}
																		</Typo>
																	)}
																</VBox>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	gap: "10px",
																	flexWrap: "wrap",
																	justifyContent: "flex-end"
																}}
															>
																{isActive ? <StatusBadge value='enabled' /> : <Typo sx={{ color: T.muted, fontSize: "0.8125rem" }}>Off</Typo>}
																{isActive && (
																	<Fields style={{ display: "block" }}>
																		<label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>Order<input type='number' value={providerSortOrders[code] ?? ""} onChange={(event) => setProviderSortOrders((current) => ({ ...current, [code]: event.target.value }))} style={{ width: 72, minHeight: 32, padding: "5px 8px" }} /></label>
																	</Fields>
																)}
																{isActive ? (
																	<>
																		<Button
																			variant='secondarySmall'
																			size='small'
																			disabled={providersLoading}
																			onClick={() =>
																				handleProviderToggle(
																					providersCompany.uid,
																					code,
																					true
																				)
																			}
																		>
																			Save order
																		</Button>
																		<Button
																			variant='secondarySmall'
																			size='small'
																			title='Disable Provider'
																			sx={{
																				color: T.danger,
																				borderColor: T.dangerLine
																			}}
																			disabled={providersLoading}
																			onClick={() =>
																				handleProviderToggle(
																					providersCompany.uid,
																					code,
																					false
																				)
																			}
																		>
																			Disable
																		</Button>
																	</>
																) : (
																		<Button
																			variant='primarySmall'
																			size='small'
																			title='Enable Provider'
																			disabled={providersLoading}
																			onClick={() =>
																				handleProviderToggle(
																					providersCompany.uid,
																					code,
																					true
																				)
																			}
																		>
																			Enable
																		</Button>
																	)}
																</Box>
															</Box>
														)
													})}
												</Box>
											</Box>
										</Tile>
									</VBox>
								)}
								{token && <Disclosure title='Engineering tools' hint='Raw issuer BIN catalogue, for developers.'><SuperAdminProviderBins token={token} /></Disclosure>}
							</VBox>
						)}

						{activeTab === "transactions" && token && <SuperAdminTransactions token={token} />}
						{activeTab === "fees" && token && <SuperAdminFees token={token} />}
						{activeTab === "company" && token && <SuperAdminCompany
							key={companyRoute.uid}
							token={token}
							company={routeCompany}
							section={companyRoute.section}
							cardUid={companyRoute.card}
							onSection={(section) => openCompany(companyRoute.uid, section)}
							onOpenCard={(card) => openCompany(companyRoute.uid, undefined, card)}
							onOpenUser={setOverviewModalUid}
							onBack={() => window.history.length > 1 ? window.history.back() : setActiveTab("companies")}
							loadAccounts={async () => routeCompany ? (await fetchCompanyFinancialAccounts(routeCompany)).map((account) => ({ ...account })) : []}
							actions={{
								fund: () => { if (routeCompany) void openCompanyTopup(routeCompany) },
								adjust: (direction) => { if (routeCompany) void openCompanyAdjustPopup(direction, routeCompany) },
								admins: () => { if (!routeCompany) return; setSelectedCompanyForAdmins(routeCompany); fetchAdmins(routeCompany.uid); setActiveTab("company_admins") },
								toggleStatus: () => {
									if (!routeCompany) return
									if (routeCompany.status === "disabled") return void handleCompanyStateChange(routeCompany.uid, "enable")
									const company = routeCompany
									void askConfirm({ title: `Disable ${company.name}?`, body: "Its users lose access to the dashboard and cards until you enable the company again.", confirmLabel: "Disable company", danger: true })
										.then((ok) => { if (ok) void handleCompanyStateChange(company.uid, "disable") })
								},
								adjustCard: (direction, card: CompanyCard) => openCardAdjustPopup(direction, card),
								reconcileCard: (card: CompanyCard) => setReconcileCard(card)
							}}
						/>}
						{activeTab === "transaction_details" && token && <SuperAdminTransactionDetails token={token} />}
						{activeTab === "kyc" && token && <SuperAdminKyc token={token} />}
						{activeTab === "user_overview" && token && <SuperAdminUserOverview token={token} onShowAllEvents={(uid) => { setEventsInitialFilters({ user: uid }); setActiveTab("events") }} />}
						{activeTab === "turnover" && token && <SuperAdminTurnover token={token} />}
						{activeTab === "jobs" && token && <SuperAdminJobs token={token} />}
						{activeTab === "crypto" && token && <SuperAdminCrypto token={token} companyName={(uid) => companies.find((item) => item.uid === uid)?.name} onOpenCompany={(uid) => openCompany(uid, "crypto")} />}
						{activeTab === "cardholders" && token && <SuperAdminCardholders token={token} />}
						{activeTab === "events" && token && <SuperAdminEvents key={JSON.stringify(eventsInitialFilters)} token={token} initialFilters={eventsInitialFilters} onOpenUser={setOverviewModalUid} onOpenCompany={(uid) => { setCompanyDirectoryFilter((previous) => ({ ...previous, query: uid })); setActiveTab("companies") }} />}
					</Box>
				</VBox>
			</Box>

			{overviewModalUid && token && <SuperAdminUserOverviewModal token={token} userUid={overviewModalUid} onClose={() => setOverviewModalUid(null)} onShowAllEvents={(uid) => { setOverviewModalUid(null); setEventsInitialFilters({ user: uid }); setActiveTab("events") }} />}
			{spendCard && token && <SuperAdminCardSpend token={token} card={spendCard} onClose={() => setSpendCard(null)} />}
			{reconcileCard && token && <SuperAdminCardReconcile token={token} card={reconcileCard} onClose={() => setReconcileCard(null)} />}

			{/* Adjustment Popup */}
			{adjustPopup && (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(17,17,22,.42)",
						backdropFilter: "blur(3px)",
						animation: "bo-fade .2s ease both",
						zIndex: 10000,
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
					onClick={(e) => {
						if (e?.target === e?.currentTarget) setAdjustPopup(null)
					}}
				>
					<VBox
						sx={{
							width: "min(420px, calc(100vw - 32px))",
							maxHeight: "calc(100dvh - 32px)",
							overflowY: "auto",
							boxSizing: "border-box",
							backgroundColor: T.surface,
							border: `1px solid ${T.border}`,
							borderRadius: "16px",
							padding: "clamp(20px, 5vw, 26px)",
							boxShadow: T.shadowLg,
							animation: `bo-pop .26s ${T.ease} both`,
							gap: "18px"
						}}
					>
						{/* Header */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start"
							}}
						>
							<VBox gap='4px'>
								<PanelTitle>{adjustPopup.direction === "in" ? "Add money to balance" : "Take money from balance"}</PanelTitle>
								<Typo sx={{ color: T.muted, fontSize: "0.8125rem", lineHeight: 1.5 }}>
									{adjustPopup.type === "card" ? "Card" : adjustPopup.type === "buyer" ? "Buyer" : "Company"}: {adjustPopup.name}
								</Typo>
							</VBox>
							<Button
								variant='secondarySmall'
								size='small'
								onClick={() => setAdjustPopup(null)}
								ariaLabel='Close'
							>
								✕
							</Button>
						</Box>

						<Select
							name='adjustment_issuer'
							label='Issuer account'
							values={adjustPopup.issuerOptions.map((account) => ({
								name: getAdjustmentIssuerLabel(account),
								value: String(account.issuer)
							}))}
							value={(() => {
								const account = adjustPopup.issuerOptions.find(
									(item) => item.issuer === adjustPopup.issuer
								)
								return account
									? {
											name: getAdjustmentIssuerLabel(account),
											value: String(account.issuer)
										}
									: null
							})()}
							disabled={adjustLoading || adjustPopup.issuerOptions.length <= 1}
							onChange={(value) => {
								const account = adjustPopup.issuerOptions.find(
									(item) => item.issuer === Number(value)
								)
								if (!account) return
								setAdjustPopup((current) =>
									current
										? {
												...current,
												issuer: account.issuer,
												currentBalance:
													account.balance === null ? null : Number(account.balance),
												currentCurrency: account.currency
											}
										: current
								)
								setAdjustForm((current) => ({ ...current, currency: account.currency }))
							}}
						/>
						{adjustPopup.issuerOptions.length === 0 && (
							<Typo sx={{ color: SA.danger, fontSize: "0.78rem" }}>
								No issuer is available for this account. Refresh the source data before adjusting money.
							</Typo>
						)}

						{/* Current balance */}
						<Box
							sx={{
								padding: "16px",
								borderRadius: "10px",
								backgroundColor: T.surface2,
								border: `1px solid ${T.border}`
							}}
						>
							<Typo variant='caption' sx={{ opacity: 0.6, display: "block", marginBottom: "4px" }}>
								Current balance (our records)
							</Typo>
							<Typo variant='headMedium' bold>
								{adjustPopup.loadingBalance
									? "Loading..."
									: adjustPopup.currentBalance !== null
										? formatCurrencyWithNumberToSymbolWithNumber(
												adjustPopup.currentBalance,
												adjustPopup.currentCurrency
											)
										: "—"}
							</Typo>
						</Box>

						{/* Amount */}
						<Input
							data={adjustForm}
							name='amount'
							onChange={(v) => setAdjustForm((prev) => ({ ...prev, amount: v }))}
							placeholder='0.00'
							type='number'
							label={`Amount, ${adjustPopup.currentCurrency || "USD"}`}
						/>

						{/* Submit */}
						<Button
							onClick={handleAdjustment}
							disabled={adjustLoading || adjustPopup.issuer === null}
							size='medium'
							sx={{
								width: "100%",
								justifyContent: "center",
								backgroundColor: adjustPopup.direction === "in" ? T.primary : T.danger
							}}
						>
							{adjustLoading
								? "Processing..."
								: adjustPopup.direction === "in" ? "Add money" : "Take money"}
						</Button>
					</VBox>
				</Box>
			)}
		</>
	)
}

type AdminIconName = "companies" | "team" | "key" | "wallet" | "audit" | "user" | "card" | "chart" | "provider" | "shield" | "crypto" | "logout" | "refresh" | "plus" | "minus" | "check" | "pause" | "receipt" | "scale" | "idcard" | "pulse" | "search" | "cpu" | "percent"

const AdminIcon = ({ name, size = 18 }: { name: AdminIconName; size?: number }) => {
	let content: React.ReactNode
	switch (name) {
		case "companies": content = <><path d='M4 20V8l8-4 8 4v12M8 20v-4h8v4M8 10h.01M12 10h.01M16 10h.01M8 13h.01M12 13h.01M16 13h.01' /></>; break
		case "team": content = <><path d='M16 20v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 10a4 4 0 100-8 4 4 0 000 8M17 11a4 4 0 010 8M17 3a4 4 0 010 8' /></>; break
		case "key": content = <><circle cx='8' cy='15' r='4' /><path d='M11 12l9-9M16 4l4 4M14 6l2 2' /></>; break
		case "wallet": content = <><path d='M3 7h16a2 2 0 012 2v10H5a2 2 0 01-2-2V5a2 2 0 012-2h13M16 13h5' /><circle cx='16' cy='13' r='.5' /></>; break
		case "audit": content = <><path d='M12 3v18M5 7h14M7 7l-4 7h8L7 7zM17 7l-4 7h8l-4-7z' /></>; break
		case "user": content = <><circle cx='12' cy='8' r='4' /><path d='M4 21a8 8 0 0116 0' /></>; break
		case "card": content = <><rect x='2.5' y='5' width='19' height='14' rx='3' /><path d='M3 10h18M7 15h3' /></>; break
		case "chart": content = <><path d='M4 20V10M10 20V4M16 20v-7M22 20H2' /></>; break
		case "provider": content = <><path d='M8 12h8M12 8v8M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z' /></>; break
		case "shield": content = <><path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-5' /></>; break
		case "crypto": content = <><path d='M9 4h5a4 4 0 010 8H8m1 0h6a4 4 0 010 8H9M11 2v20M15 2v3M15 19v3' /></>; break
		case "logout": content = <><path d='M10 17l5-5-5-5M15 12H3M21 19V5a2 2 0 00-2-2h-6' /></>; break
		case "refresh": content = <><path d='M20 11a8 8 0 10-2.3 5.7M20 5v6h-6' /></>; break
		case "plus": content = <><path d='M12 5v14M5 12h14' /></>; break
		case "minus": content = <><path d='M5 12h14' /></>; break
		case "check": content = <><path d='M5 12l4 4L19 6' /></>; break
		case "pause": content = <><path d='M9 5v14M15 5v14' /></>; break
		case "receipt": content = <><path d='M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6M9 16h3' /></>; break
		case "scale": content = <><path d='M12 4v16M7 20h10M5 8h14M5 8l-2.5 6a2.5 2.5 0 005 0L5 8zM19 8l-2.5 6a2.5 2.5 0 005 0L19 8z' /></>; break
		case "idcard": content = <><rect x='3' y='5' width='18' height='14' rx='2.5' /><circle cx='9' cy='11' r='2' /><path d='M6 16a3 3 0 016 0M14 10h4M14 13h3' /></>; break
		case "pulse": content = <><path d='M3 12h4l3-7 4 14 3-7h4' /></>; break
		case "percent": content = <><path d='M19 5 5 19' /><circle cx='7' cy='7' r='2.5' /><circle cx='17' cy='17' r='2.5' /></>; break
		case "search": content = <><circle cx='11' cy='11' r='6.5' /><path d='M20 20l-4.2-4.2' /></>; break
		case "cpu": content = <><rect x='6' y='6' width='12' height='12' rx='2' /><path d='M10 10h4v4h-4zM9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4' /></>; break
	}
	return <svg width={size} height={size} viewBox='0 0 24 24' fill='none' aria-hidden='true' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>{content}</svg>
}

const PanelTitle = ({ children }: { children: React.ReactNode }) =>
	<Typo sx={{ color: T.text, fontSize: "0.9375rem", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.35 }}>{children}</Typo>

const AdminBrandMark = () => (
	<Box sx={{ width: "30px", height: "30px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", color: "#FFFFFF", background: "linear-gradient(150deg, #6A61F0 0%, #4338CA 100%)", boxShadow: "0 1px 2px rgba(67,56,202,.35), inset 0 1px 0 rgba(255,255,255,.22)" }}>
		<AdminIcon name='card' size={16} />
	</Box>
)

/** One way to pick a client on screens that work per client. */
const ClientPicker = ({ companies, title, hint, onPick }: { companies: ICompany[]; title: string; hint: string; onPick: (company: ICompany) => void }) =>
	<KitPanel title={title} hint={hint}>
		<Fields style={{ maxWidth: 520 }}>
			<label>Client<select value='' onChange={(event) => {
				const company = companies.find((item) => item.uid === event.target.value)
				if (company) onPick(company)
			}}><option value='' disabled>Choose a client…</option>{companies.map((item) => <option key={item.uid} value={item.uid}>{item.name} ({item.uid})</option>)}</select></label>
		</Fields>
	</KitPanel>

const SidebarSectionLabel = ({ children }: { children: React.ReactNode }) => (
	<Typo sx={{ color: "rgba(255,255,255,.38)", fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", padding: "14px 10px 6px", [SA_COMPACT]: { display: "none" } }}>{children}</Typo>
)

const AdminMetric = ({ label, value, icon, tone = "neutral" }: { label: string; value: string; icon: AdminIconName; tone?: "neutral" | "success" | "money" }) => {
	const iconColor = tone === "success" ? T.success : tone === "money" ? T.accent : T.text2
	const iconBg = tone === "success" ? T.successSoft : tone === "money" ? T.accentSoft : T.hover
	return <VBox gap='10px' sx={{ minWidth: 0, padding: "16px 18px", borderRadius: "14px", backgroundColor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadowSm }}>
		<HBox sx={{ alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
			<Typo sx={{ color: T.muted, fontSize: "0.8125rem", fontWeight: 500 }}>{label}</Typo>
			<Box sx={{ color: iconColor, background: iconBg, width: "30px", height: "30px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}><AdminIcon name={icon} size={16} /></Box>
		</HBox>
		<Box title={value}><Typo sx={{ color: T.text, fontSize: "1.625rem", lineHeight: 1.1, fontWeight: 650, letterSpacing: "-0.03em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontVariantNumeric: "tabular-nums" }}>{value}</Typo></Box>
	</VBox>
}

const SidebarItem = ({
	label,
	active,
	onClick,
	icon
}: {
	label: string
	active: boolean
	onClick: () => void
	icon: React.ReactNode
}) => {
	return (
		<Box
			element='button'
			type='button'
			title={label}
			aria-current={active ? "page" : undefined}
			onClick={onClick}
			sx={{
				width: "100%",
				minHeight: "32px",
				padding: "6px 10px",
				border: 0,
				borderRadius: "7px",
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				gap: "10px",
				textAlign: "left",
				fontFamily: "inherit",
				backgroundColor: active ? "rgba(255,255,255,.1)" : "transparent",
				boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,.08)" : "none",
				color: active ? "#FFFFFF" : "rgba(255,255,255,.66)",
				transition: `background-color .16s ${T.ease}, color .16s ${T.ease}, box-shadow .16s ${T.ease}`,
				[SA_COMPACT]: { justifyContent: "center", padding: "8px 0" },
				"&:hover": {
					backgroundColor: active ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.06)",
					color: "#FFFFFF"
				}
			}}
		>
			<Box sx={{ width: "18px", height: "18px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: active ? "#A5A0FF" : "rgba(255,255,255,.45)" }}>{icon}</Box>
			<Typo variant='text' sx={{ color: "inherit", fontSize: "0.8125rem", fontWeight: active ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", [SA_COMPACT]: { display: "none" } }}>
				{label}
			</Typo>
		</Box>
	)
}
